import { useTheme as usarTema } from "@emotion/react";
import {
  faAngleDown,
  faAngleLeft,
  faAngleRight,
  faArrowDown,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import ViewColumnRoundedIcon from "@mui/icons-material/ViewColumnRounded";
import { MenuItem, Skeleton } from "@mui/material";
import { usePagination } from "@table-library/react-table-library/pagination";
import {
  SelectClickTypes,
  SelectTypes,
  useRowSelect,
} from "@table-library/react-table-library/select";
import { HeaderCellSort, SortToggleType, useSort } from "@table-library/react-table-library/sort";
import {
  Body,
  Cell,
  Footer,
  FooterCell,
  FooterRow,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table,
} from "@table-library/react-table-library/table";
import { useTheme } from "@table-library/react-table-library/theme";
import { CellTree, TreeExpandClickTypes, useTree } from "@table-library/react-table-library/tree";
import dayjs from "dayjs";
import { debounce } from "lodash";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as ArrowUpDownDark } from "../../assets/icons/ArrowUpDownDark.svg";
import { ReactComponent as ArrowUpDownLight } from "../../assets/icons/ArrowUpDownLight.svg";
import { useLoader } from "../../contexts/loader";
import { TemaCustomizadoContext } from "../../contexts/theme";
import Carregando from "../../pages/carregando";
import formatarReal from "../../utils/formatarReal.js";
import tornarSlider from "../../utils/tornarSlider";
import useTamanhoTela from "../../utils/useTamanhoTela";
import { BotaoAzulVazado, BotaoVermelhoVazado } from "../Botao";
import {
  CampoDeData,
  CampoDeDataSemHora,
  CampoDeInputNumerico,
  CampoDeSelecao,
  CampoDeTexto,
} from "../CampoInput";
import { CheckBox } from "../Checkbox";
import { TabelaSimples } from "../TabelaSimples";
import { dadosMokadosTabela, itensDiasDaSemana } from "./dadosMokadosTabela";
import * as S from "./styles.js";
import { useAuth } from "../../contexts/auth";

const DEBOUNCE_MS = 800;
const EXPIRACAO_STORAGE_MS = 30 * 24 * 60 * 60 * 1000;

const converterParaDayjsDataHora = (valor) => {
  if (!valor) return null;
  if (dayjs.isDayjs(valor)) return valor;

  if (typeof valor === "string") {
    const partes = valor.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+-)?\s+(\d{2}):(\d{2})$/);

    if (partes) {
      const [, dia, mes, ano, hora, minuto] = partes;
      return dayjs(`${ano}-${mes}-${dia}T${hora}:${minuto}`);
    }
  }

  return dayjs(valor);
};

let textosInputs;
let quantidadePaginacao = null;
let linhaSelecionada = null;

const gerarChaveStorageTabela = (prefixo, identificadorTabela = "", idEmpresa = "") => {
  try {
    const url = window.location.pathname;
    const sufixo = identificadorTabela ? `_${identificadorTabela}` : "";
    return `${prefixo}_${url}${sufixo}_${idEmpresa}`.replace(/[^a-zA-Z0-9_]/g, "_");
  } catch {
    return `${prefixo}_default${identificadorTabela ? `_${identificadorTabela}` : ""}_${idEmpresa}`;
  }
};

const carregarStorageTabela = (chave) => {
  try {
    const configuracaoSalva = localStorage.getItem(chave);

    if (!configuracaoSalva) {
      return null;
    }

    const config = JSON.parse(configuracaoSalva);

    if (config.timestamp && Date.now() - config.timestamp < EXPIRACAO_STORAGE_MS) {
      return config;
    }

    return null;
  } catch (error) {
    console.error("Erro ao carregar configuracao da tabela:", error);
    return null;
  }
};

function TabelaLibComponent({
  itemUnico = false,
  dadosTabela = undefined,
  variante = {},
  dadosMokados = false,
  porModulo = false,
  recebimentoMercadoria = false,
  headerSecundario = false,
  textoPorModulo = "",
  linhaValorTela,
  key,
  desabilitarPortalAutoComplete = false,
  focoAutomaticoFiltro = false,
  identificadorTabela = "",
  layoutFlexivel = false,
  variantNcmCest = false,
  comHora = false,
  defaultStatusAtivo = false,
  estacaoImpressao = false,
  fixedFooter = false,
  onLimparFiltros,
}) {
  const { t } = useTranslation("common");
  dadosTabela = dadosMokados ? dadosMokadosTabela : dadosTabela;
  const { carregandoTabela } = useLoader();
  const tema = usarTema();
  const { getTemaDarkAtivo } = useContext(TemaCustomizadoContext);
  const booleanTema = getTemaDarkAtivo();
  const { dadosAuth } = useAuth();
  const idEmpresa = dadosAuth?.empresaDTO?.id;
  const [statusPesquisaColuna, setStatusPesquisaColuna] = useState(
    variante?.filtroColunaVisivel || false,
  );
  const [valorInputGeral, setValorInputGeralState] = useState("");
  const [valorFiltroGeralDebounced, setValorFiltroGeralDebounced] = useState("");
  const [valorInputColuna, setValorInputColuna] = useState({});
  const [valorFiltroColunasDebounced, setValorFiltroColunasDebounced] = useState({});
  const [autocompleteTravadoAberto, setAutocompleteTravadoAberto] = useState(null);
  const filtrosColunasRef = useRef({});
  const { largura } = useTamanhoTela();
  const [diaDaSemana, setDiaDaSemana] = useState("Selecione");
  const [ordenacaoColunas, setOrdenacaoColunas] = useState({});
  const refTabelaScroll = useRef();
  const refTabelaAgupadaScroll = useRef();
  const refCampoFiltroGeral = useRef();
  const posicaoScrollHorizontalRef = useRef(0);
  const posicaoScrollPaginacaoRef = useRef(null);
  const efeitoCheckboxMontou = useRef(true);
  const primeiraExecucaoValoresColunaRef = useRef(true);
  const configuracaoColunasInicializada = useRef(false);

  useEffect(() => {
    if (!autocompleteTravadoAberto) return undefined;

    const fecharAoRolar = (evento) => {
      const rolouDentroDaLista = evento.target?.closest?.(".MuiAutocomplete-popper");

      if (!rolouDentroDaLista) {
        setAutocompleteTravadoAberto(null);
      }
    };

    const fecharAoRedimensionar = () => setAutocompleteTravadoAberto(null);

    document.addEventListener("scroll", fecharAoRolar, true);
    window.addEventListener("resize", fecharAoRedimensionar);

    return () => {
      document.removeEventListener("scroll", fecharAoRolar, true);
      window.removeEventListener("resize", fecharAoRedimensionar);
    };
  }, [autocompleteTravadoAberto]);
  const [quantidadeItensMostrarTabela, setQuantidadeItensMostrarTabela] = useState([]);
  const [quantidadeItens, setQuantidadeItens] = useState(
    itemUnico
      ? "1"
      : variante?.tamanhoPaginaInicial
        ? String(variante.tamanhoPaginaInicial)
        : quantidadePaginacao !== null
          ? quantidadePaginacao
          : "10",
  );
  const [primeiroCarregamento, setPrimeiroCarregamento] = useState(true);
  const [inicializacaoCompleta, setInicializacaoCompleta] = useState(false);
  const [colunasVisiveis, setColunasVisiveis] = useState(
    dadosTabela.colunas?.map((coluna) => coluna.key),
  );
  const alturaContainerTabela = variante?.alturaContainerTabela;
  const [alturaCorpoTabelaFixa, setAlturaCorpoTabelaFixa] = useState(null);

  const gerarChaveFiltroGeral = () => {
    try {
      const url = window.location.pathname;
      const sufixo = identificadorTabela ? `_${identificadorTabela}` : "";
      return `tabela_filtro_geral_${url}${sufixo}_${idEmpresa}`.replace(/[^a-zA-Z0-9_]/g, "_");
    } catch {
      return `tabela_filtro_geral_default${identificadorTabela ? `_${identificadorTabela}` : ""}_${idEmpresa}`;
    }
  };

  const gerarChaveFiltroColunas = () => {
    try {
      const url = window.location.pathname;
      const sufixo = identificadorTabela ? `_${identificadorTabela}` : "";
      return `tabela_filtro_colunas_${url}${sufixo}_${idEmpresa}`.replace(/[^a-zA-Z0-9_]/g, "_");
    } catch {
      return `tabela_filtro_colunas_default${identificadorTabela ? `_${identificadorTabela}` : ""}_${idEmpresa}`;
    }
  };

  const carregarFiltroGeralStorage = () => {
    try {
      const chave = gerarChaveFiltroGeral();
      const configuracaoSalva = localStorage.getItem(chave);

      if (configuracaoSalva) {
        const config = JSON.parse(configuracaoSalva);
        // Verifica se não é muito antiga (30 dias)
        if (config.timestamp && Date.now() - config.timestamp < EXPIRACAO_STORAGE_MS) {
          return config.valorFiltroGeral || "";
        }
      }
      return "";
    } catch (error) {
      console.error("Erro ao carregar filtro geral:", error);
      return "";
    }
  };

  const salvarFiltroGeralStorage = (valor) => {
    try {
      const chave = gerarChaveFiltroGeral();
      if (valor && String(valor)?.trim() !== "") {
        const configuracao = {
          valorFiltroGeral: valor,
          timestamp: Date.now(),
          versao: "1.0",
        };
        localStorage.setItem(chave, JSON.stringify(configuracao));
      } else {
        localStorage.removeItem(chave);
      }
    } catch (error) {
      console.error("Erro ao salvar filtro geral:", error);
    }
  };

  const carregarFiltroColunasStorage = () => {
    try {
      const chave = gerarChaveFiltroColunas();
      const configuracaoSalva = localStorage.getItem(chave);

      if (configuracaoSalva) {
        const config = JSON.parse(configuracaoSalva);
        // Verifica se não é muito antiga (30 dias)
        if (config.timestamp && Date.now() - config.timestamp < EXPIRACAO_STORAGE_MS) {
          return config;
        }
      }
      return null;
    } catch (error) {
      console.error("Erro ao carregar filtros de colunas:", error);
      return null;
    }
  };

  const salvarFiltroColunasStorage = (filtrosColunas, statusPesquisa) => {
    try {
      const chave = gerarChaveFiltroColunas();

      if (filtrosColunas && Object.keys(filtrosColunas).length > 0) {
        const configuracao = {
          filtrosColunas: filtrosColunas,
          statusPesquisaColuna: statusPesquisa,
          timestamp: Date.now(),
          versao: "1.0",
        };
        localStorage.setItem(chave, JSON.stringify(configuracao));
      } else {
        localStorage.removeItem(chave);
      }
    } catch (error) {
      console.error("Erro ao salvar filtros de colunas:", error);
    }
  };

  const valorFiltroValido = (valor) => {
    if (valor === null || valor === undefined || valor === "") return false;

    if (Array.isArray(valor)) {
      return valor.some((item) => valorFiltroValido(item));
    }

    if (typeof valor === "object") {
      return Object.values(valor).some((item) => valorFiltroValido(item));
    }

    return true;
  };

  const setValorInputGeral = (valor) => {
    setValorInputGeralState(valor);
    salvarFiltroGeralStorage(valor);
  };

  const limparCampoFiltroGeral = () => {
    setValorInputGeral("");
    if (refCampoFiltroGeral.current) {
      refCampoFiltroGeral.current.focus();
    }
  };

  const temFiltrosSalvos = () => {
    try {
      const chaveFiltroGeral = gerarChaveFiltroGeral();
      const filtroGeralSalvo = localStorage.getItem(chaveFiltroGeral);
      let temFiltroGeralValido = false;

      if (filtroGeralSalvo) {
        const config = JSON.parse(filtroGeralSalvo);
        temFiltroGeralValido =
          config.valorFiltroGeral && String(config.valorFiltroGeral).trim() !== "";
      }

      const chaveFiltroColunas = gerarChaveFiltroColunas();
      const filtroColunasSalvo = localStorage.getItem(chaveFiltroColunas);
      let temFiltrosColunasValidos = false;

      if (filtroColunasSalvo) {
        const config = JSON.parse(filtroColunasSalvo);

        if (config.filtrosColunas) {
          temFiltrosColunasValidos = Object.values(config.filtrosColunas).some(
            (valor) => valor !== null && valor !== undefined && String(valor).trim() !== "",
          );
        }
      }

      const colunasPadrao = [...colunasDefaultEscondidas].sort();

      let colunasEscondidasSalvas = false;

      if (variante?.esconderColuna && dadosTabela.colunas?.length) {
        const chaveColunasEscondidas = gerarChaveLocalStorage();
        const colunasSalvas = localStorage.getItem(chaveColunasEscondidas);

        if (colunasSalvas) {
          const config = JSON.parse(colunasSalvas);

          const colunasSalvasOrdenadas = [...(config.colunasEscondidas || [])].sort();

          colunasEscondidasSalvas =
            JSON.stringify(colunasSalvasOrdenadas) !== JSON.stringify(colunasPadrao);
        }
      }

      // Verificar se há filtro geral ativo no estado
      const temFiltroGeralAtivo = valorInputGeral && String(valorInputGeral).trim() !== "";

      const temFiltrosColunasAtivos =
        valorInputColuna &&
        Object.values(valorInputColuna).some(
          (valor) =>
            valor !== null &&
            valor !== undefined &&
            String(valor).trim() !== "" &&
            String(valor).toUpperCase() !== "TODOS" &&
            String(valor).toUpperCase() !== "SELECIONE",
        );

      const colunasAtuais = [...colunasEscondidas].sort();

      const temColunasEscondidasAtivas =
        JSON.stringify(colunasAtuais) !== JSON.stringify(colunasPadrao);

      return (
        temFiltroGeralValido ||
        temFiltrosColunasValidos ||
        colunasEscondidasSalvas ||
        temFiltroGeralAtivo ||
        temFiltrosColunasAtivos ||
        temColunasEscondidasAtivas
      );
    } catch (error) {
      console.error("Erro ao verificar filtros salvos:", error);
      return false;
    }
  };

  const limparFiltrosSalvos = () => {
    try {
      localStorage.removeItem(gerarChaveFiltroGeral());
      localStorage.removeItem(gerarChaveFiltroColunas());
      if (variante?.esconderColuna && dadosTabela.colunas?.length) {
        localStorage.removeItem(gerarChaveLocalStorage());
      }

      pegarValorFiltroGeralDebounced.cancel();
      atualizarFiltroColunasDebounced.cancel();

      const todasColunas = dadosTabela.colunas?.map((coluna) => coluna.key) || [];
      const novosTextosInputs = todasColunas
        .filter((nomeColuna) => nomeColuna !== "Ações")
        .reduce((obj, coluna) => {
          obj[coluna] = null;
          const colunaConfig = dadosTabela.colunas?.find((c) => c.key === coluna);
          obj[coluna] = colunaConfig?.valorPadrao || null;
          return obj;
        }, {});

      setValorInputGeralState("");
      setValorFiltroGeralDebounced("");
      setValorInputColuna(novosTextosInputs);
      filtrosColunasRef.current = novosTextosInputs;
      textosInputs = novosTextosInputs;
      setValorFiltroColunasDebounced(novosTextosInputs);
      setColunasEscondidas([]);
      setColunasVisiveis(todasColunas);
      setColunasKeyEscondidas([]);
      setStatusPesquisaColuna(false);

      const colunasPadraoEscondidas = dadosTabela.colunas
        .filter((c) => c.defaultEscondida)
        .map((c) => c.titulo);

      const keysPadraoEscondidas = dadosTabela.colunas
        .filter((c) => c.defaultEscondida)
        .map((c) => c.key);

      setColunasEscondidas(colunasPadraoEscondidas);
      setColunasKeyEscondidas(keysPadraoEscondidas);

      setColunasVisiveis(
        dadosTabela.colunas.map((c) => c.key).filter((key) => !keysPadraoEscondidas.includes(key)),
      );

      if (variante?.filtroColuna || variante === "completa") {
        salvarFiltroColunasStorage(novosTextosInputs, false);
      }
      onLimparFiltros?.();
    } catch (error) {
      console.error("Erro ao limpar filtros salvos:", error);
    }
  };

  const gerarChaveLocalStorage = () => {
    try {
      const url = window.location.pathname;
      const colunasKeys = dadosTabela.colunas?.map((c) => c.key).join("_") || "default";
      const sufixo = identificadorTabela ? `_${identificadorTabela}` : "";
      return `tabela_colunas_${url}_${colunasKeys}${sufixo}_${idEmpresa}`.replace(
        /[^a-zA-Z0-9_]/g,
        "_",
      );
    } catch {
      return `tabela_colunas_default${identificadorTabela ? `_${identificadorTabela}` : ""}_${idEmpresa}`;
    }
  };

  const carregarConfiguracaoColunasStorage = () => {
    try {
      if (!dadosTabela.colunas?.length || !variante?.esconderColuna) return null;

      const chave = gerarChaveLocalStorage();
      const configuracaoSalva = localStorage.getItem(chave);

      if (configuracaoSalva) {
        const config = JSON.parse(configuracaoSalva);
        const trintaDias = 30 * 24 * 60 * 60 * 1000;
        if (config.timestamp && Date.now() - config.timestamp < trintaDias) {
          return config;
        }
      }
      return null;
    } catch (error) {
      console.error("Erro ao carregar configuração das colunas:", error);
      return null;
    }
  };

  useEffect(() => {
    if (
      configuracaoColunasInicializada.current ||
      !dadosTabela.colunas?.length ||
      !variante?.esconderColuna
    ) {
      return;
    }

    configuracaoColunasInicializada.current = true;

    try {
      const configuracaoSalva = carregarConfiguracaoColunasStorage();

      const colunasPadraoEscondidas = dadosTabela.colunas
        .filter((c) => c.defaultEscondida === true)
        .map((c) => c.titulo);

      if (configuracaoSalva?.colunasEscondidas?.length > 0) {
        setColunasEscondidas(configuracaoSalva.colunasEscondidas);
        setColunasVisiveis(configuracaoSalva.colunasVisiveis);
        setColunasKeyEscondidas(configuracaoSalva.colunasKeyEscondidas);
      } else {
        setColunasEscondidas(colunasPadraoEscondidas);

        const keysEscondidas = dadosTabela.colunas
          .filter((c) => c.defaultEscondida === true)
          .map((c) => c.key);

        setColunasKeyEscondidas(keysEscondidas);

        setColunasVisiveis(
          dadosTabela.colunas.map((c) => c.key).filter((k) => !keysEscondidas.includes(k)),
        );
      }
    } catch (error) {
      console.error("Erro ao carregar configuração inicial:", error);
    }
  }, [dadosTabela.colunas]);

  useEffect(() => {
    const valorSalvo = carregarFiltroGeralStorage();
    if (valorSalvo) {
      setValorInputGeralState(valorSalvo);
      setValorFiltroGeralDebounced(valorSalvo);
    }
  }, []);

  useEffect(() => {
    if (variante?.filtroColuna || variante === "completa") {
      try {
        const configuracaoSalva = carregarFiltroColunasStorage();
        const filtrosSalvos = configuracaoSalva?.filtrosColunas;
        const possuiFiltrosSalvos = valorFiltroValido(filtrosSalvos);

        if (configuracaoSalva) {
          if (configuracaoSalva.statusPesquisaColuna !== undefined) {
            setStatusPesquisaColuna(configuracaoSalva.statusPesquisaColuna);
          }

          if (possuiFiltrosSalvos) {
            setValorInputColuna(filtrosSalvos);
            filtrosColunasRef.current = filtrosSalvos;
            setValorFiltroColunasDebounced(filtrosSalvos);
            textosInputs = filtrosSalvos;
          } else {
            filtrosColunasRef.current = {};
            textosInputs = {};
          }
        } else {
          filtrosColunasRef.current = {};
          textosInputs = {};
        }
      } catch (error) {
        console.error("Erro ao carregar filtros de colunas:", error);
        filtrosColunasRef.current = {};
        textosInputs = {};
      }
    }
    // Marcar inicialização como completa após carregar todos os filtros
    // Se a requisição inicial já foi feita (ex: no ModalSelecionarEmpresasPopup),
    // aguardar um tempo para evitar conflitos
    if (variante?.desativarBuscaInternaInicial || variante?.funcaoBuscaInicial) {
      setTimeout(() => setInicializacaoCompleta(true), 500);
    } else {
      setInicializacaoCompleta(true);
    }
  }, []);

  useEffect(() => {
    if (!refTabelaScroll.current) return;

    const container = refTabelaScroll.current;
    const tbody = container.querySelector("tbody");
    const theads = container.querySelectorAll("thead");
    const tfoots = container.querySelectorAll("tfoot");

    if (!tbody) return;

    const linhasFooter = Array.from(tfoots)
      .map((tfoot) => tfoot.querySelector("tr"))
      .filter(Boolean);
    const minWidthsOriginaisFooter = linhasFooter.map((linha) => linha.style.minWidth);

    if (variante?.preservarScrollHorizontal && variante?.scrollHorizontalAbaixoFooter) {
      const larguraConteudoTabela = Math.max(
        tbody.scrollWidth,
        ...Array.from(theads).map((thead) => thead.scrollWidth),
      );

      linhasFooter.forEach((linha) => {
        linha.style.minWidth = `${larguraConteudoTabela}px`;
      });
    }

    const scrollHorizontal = variante?.scrollHorizontalAbaixoFooter ? tfoots[0] || tbody : tbody;
    const cleanupSliderHorizontal = tornarSlider(scrollHorizontal);
    const cleanupSliderTbody = scrollHorizontal !== tbody ? tornarSlider(tbody) : null;

    const sincronizarScrollHorizontal = (scrollLeft, origem) => {
      if (tbody !== origem) tbody.scrollLeft = scrollLeft;
      theads.forEach((thead) => {
        thead.scrollLeft = scrollLeft;
      });
      tfoots.forEach((tfoot) => {
        if (tfoot !== origem) tfoot.scrollLeft = scrollLeft;
      });
    };

    const syncScroll = (e) => {
      if (
        variante?.preservarScrollHorizontal &&
        variante?.scrollHorizontalAbaixoFooter &&
        e.target.scrollWidth <= e.target.clientWidth
      ) {
        return;
      }

      if (variante?.preservarScrollHorizontal) {
        posicaoScrollHorizontalRef.current = e.target.scrollLeft;
      }
      sincronizarScrollHorizontal(e.target.scrollLeft, e.target);
    };

    const scrollHorizontalComShift = (e) => {
      if (!variante?.scrollHorizontalAbaixoFooter || !e.shiftKey || !scrollHorizontal) return;

      const deltaHorizontal = e.deltaX || e.deltaY;
      if (!deltaHorizontal) return;

      const limiteScroll = scrollHorizontal.scrollWidth - scrollHorizontal.clientWidth;
      if (limiteScroll <= 0) return;

      const novoScroll = Math.max(
        0,
        Math.min(limiteScroll, scrollHorizontal.scrollLeft + deltaHorizontal),
      );

      if (novoScroll === scrollHorizontal.scrollLeft) return;

      e.preventDefault();
      scrollHorizontal.scrollLeft = novoScroll;
      sincronizarScrollHorizontal(novoScroll, scrollHorizontal);
    };

    tbody.addEventListener("scroll", syncScroll);
    tbody.addEventListener("wheel", scrollHorizontalComShift, {
      passive: false,
    });
    tfoots.forEach((tfoot) => tfoot.addEventListener("scroll", syncScroll));

    const restaurarScrollHorizontal = () => {
      const limiteScroll = Math.max(0, scrollHorizontal.scrollWidth - scrollHorizontal.clientWidth);
      const deveCentralizarTabela =
        variante?.centralizarTabelaSemResultados && !dadosTabela.nodes?.length;
      const posicaoRestaurada = deveCentralizarTabela
        ? limiteScroll / 2
        : Math.min(posicaoScrollHorizontalRef.current, limiteScroll);

      if (deveCentralizarTabela) {
        posicaoScrollHorizontalRef.current = posicaoRestaurada;
      }

      scrollHorizontal.scrollLeft = posicaoRestaurada;
      sincronizarScrollHorizontal(posicaoRestaurada, scrollHorizontal);
    };
    const possuiAnimationFrame = typeof requestAnimationFrame === "function";
    const frameRestauracao = variante?.preservarScrollHorizontal
      ? possuiAnimationFrame
        ? requestAnimationFrame(restaurarScrollHorizontal)
        : setTimeout(restaurarScrollHorizontal, 0)
      : null;

    return () => {
      if (frameRestauracao !== null) {
        if (possuiAnimationFrame) {
          cancelAnimationFrame(frameRestauracao);
        } else {
          clearTimeout(frameRestauracao);
        }
      }
      tbody.removeEventListener("scroll", syncScroll);
      tbody.removeEventListener("wheel", scrollHorizontalComShift);
      tfoots.forEach((tfoot) => tfoot.removeEventListener("scroll", syncScroll));
      cleanupSliderHorizontal?.();
      cleanupSliderTbody?.();
      linhasFooter.forEach((linha, index) => {
        linha.style.minWidth = minWidthsOriginaisFooter[index];
      });
    };
  }, [
    refTabelaScroll.current,
    dadosTabela.nodes?.length,
    variante?.centralizarTabelaSemResultados,
    variante?.preservarScrollHorizontal,
    variante?.scrollHorizontalAbaixoFooter,
  ]);

  useEffect(() => {
    if (focoAutomaticoFiltro && refCampoFiltroGeral.current) {
      const timer = setTimeout(() => {
        refCampoFiltroGeral.current.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [focoAutomaticoFiltro]);

  const formatarInscricaoFederal = (value) => {
    try {
      if (/[a-zA-Z]/.test(value)) {
        return value;
      }

      let valorFormatado = value.replace(/\D/g, "");

      if (/^\d+$/.test(valorFormatado)) {
        if (valorFormatado.length <= 11) {
          valorFormatado = valorFormatado
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d{1,2})/, "$1-$2")
            .replace(/(-\d{2})\d+?$/, "$1");
        } else {
          valorFormatado = valorFormatado
            .replace(/(\d{2})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1.$2")
            .replace(/(\d{3})(\d)/, "$1/$2")
            .replace(/(\d{4})(\d{1,2})/, "$1-$2")
            .replace(/(-\d{2})\d+?$/, "$1");
        }
      }

      return valorFormatado;
    } catch (error) {
      console.error(error);
      return value;
    }
  };

  const opcoesSelectStatus = [
    {
      value: "ATIVO",
      label: t("status.active"),
    },
    {
      value: "INATIVO",
      label: t("status.inactive"),
    },
    {
      value: "TODOS",
      label: t("status.all"),
    },
  ];

  useEffect(() => {
    const root = refTabelaScroll?.current;
    if (!root) return;

    const thead = root.querySelector("thead");
    const tbody = root.querySelector("tbody");

    if (!thead || !tbody) return;

    const handleScroll = () => {
      thead.scrollLeft = tbody.scrollLeft;
    };

    tbody.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      tbody.removeEventListener("scroll", handleScroll);
    };
  }, [dadosTabela]);

  function valorSelectStatus(valor, colunaKey, manterTodosNoFiltro = false) {
    const valorParaFiltro = valor === "TODOS" && !manterTodosNoFiltro ? null : valor;

    setValorInputColuna((prev) => {
      const novosValores = { ...prev, [colunaKey]: valor };
      filtrosColunasRef.current = { ...prev, [colunaKey]: valorParaFiltro };
      textosInputs = { ...prev, [colunaKey]: valorParaFiltro };

      if (variante?.filtroColuna || variante === "completa") {
        salvarFiltroColunasStorage(novosValores, statusPesquisaColuna);
      }

      return novosValores;
    });

    atualizarFiltroColunasDebounced();
  }

  useEffect(() => {
    if (primeiroCarregamento && variante?.esconderColuna) {
      blurTabela(1800);
    }
  }, [dadosTabela]);

  useEffect(() => {}, [dadosTabela, linhaValorTela]);

  const sort = useSort(
    dadosTabela,
    {
      onChange: onSortChange,
    },
    {
      sortIcon: {
        margin: "10px",
        iconDefault: booleanTema ? <ArrowUpDownDark /> : <ArrowUpDownLight />,
        iconUp: <FontAwesomeIcon icon={faArrowDown} />,
        iconDown: <FontAwesomeIcon icon={faArrowUp} />,
      },
      sortToggleType: SortToggleType.AlternateWithReset,
      isServer: true,
      sortFns: {
        TEXTO: "none",
      },
    },
  );

  function mudarValoresSelect(displayValue, itemsSelect) {
    const item = itemsSelect.find((item) => item.displayValue === displayValue);
    return item ? item.value : null;
  }

  function onSortChange(action, state) {
    let direcao;
    let coluna = action.payload.value.sortKey;

    if (state.reverse) {
      direcao = "DESC";
    } else {
      direcao = "ASC";
    }

    if (state.sortKey === "NONE") {
      coluna = "id"; //
      direcao = "";
    }

    setOrdenacaoColunas({ direcao, coluna });
  }

  const data = { nodes: dadosTabela };
  const tree = useTree(
    data,
    {
      state: {
        ids: variante?.treesAbertas ? variante?.treesAbertas : [],
      },
      onChange: onTreeChange,
    },
    {
      treeIcon: {
        margin: "10px",
        iconRight: (
          <FontAwesomeIcon icon={faAngleRight} style={{ color: tema.cores.actionActive }} />
        ),
        iconDown: <FontAwesomeIcon icon={faAngleDown} style={{ color: tema.cores.actionActive }} />,
      },
      clickType: TreeExpandClickTypes.ButtonClick,
    },
  );

  function onTreeChange(action, state) {}

  // features

  const gerarBotoes = (pagina, paginas, maxBotoes = 5) => {
    const botao = (e) => (
      <S.BotaoPagina
        type="button"
        key={e}
        disabled={e === pagina}
        accent="#000000"
        color={e === pagina ? "#CBE6FF" : "transparent"}
        style={{
          color: e === pagina ? tema.cores.textoInverso : tema.cores.textoSimples,
          backgroundColor: e === pagina ? tema.cores.actionPressed : "transparent",
        }}
        selected={e === pagina}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          event.preventDefault();
          mudarPaginaTabela(e);
        }}
      >
        {e + 1}
      </S.BotaoPagina>
    );

    maxBotoes = largura > 767 ? 5 : 3;
    const quantidadeBotoesMostrados = Math.min(paginas, maxBotoes);
    const metadeMaxBotoes = (maxBotoes - 1) / 2;
    let botaoInicial = 0;
    const ultimoBotao = paginas - 1;

    // Booleano
    const todosBotoesSendoMostrados = paginas === quantidadeBotoesMostrados;

    // Booleano
    const chegandoNosUltimosBotoes = pagina + metadeMaxBotoes > ultimoBotao;

    if (!todosBotoesSendoMostrados) {
      let offset = metadeMaxBotoes;

      if (chegandoNosUltimosBotoes) offset = offset + (pagina + metadeMaxBotoes - ultimoBotao);

      botaoInicial = Math.max(0, pagina - offset);
    }

    return Array.from({ length: quantidadeBotoesMostrados }, (_, index) =>
      botao(index + botaoInicial),
    );
  };

  const pagination = usePagination(
    data,
    {
      state: {
        page: itemUnico ? dadosTabela.pagina : 0,
      },
    },
    {
      isServer: true,
    },
  );

  const [colunasEscondidas, setColunasEscondidas] = useState([]);
  const [colunasKeyEscondidas, setColunasKeyEscondidas] = useState([]);
  const colunasDefaultEscondidas = useMemo(() => {
    return (
      dadosTabela.colunas
        ?.filter((coluna) => coluna.defaultEscondida)
        .map((coluna) => coluna.titulo) || []
    );
  }, [dadosTabela.colunas]);

  const toggleColumn = (column, keyColumn) => {
    let novasColunasEscondidas, novasColunasVisiveis, novasColunasKeyEscondidas;

    if (colunasEscondidas.includes(column)) {
      novasColunasEscondidas = colunasEscondidas.filter((v) => v !== column);
      setColunasEscondidas(novasColunasEscondidas);
    } else {
      novasColunasEscondidas = colunasEscondidas.concat(column);
      setColunasEscondidas(novasColunasEscondidas);
    }

    if (colunasVisiveis.includes(keyColumn)) {
      novasColunasVisiveis = colunasVisiveis.filter((v) => v !== keyColumn);
      setColunasVisiveis(novasColunasVisiveis);
    } else {
      novasColunasVisiveis = colunasVisiveis.concat(keyColumn);
      setColunasVisiveis(novasColunasVisiveis);
    }

    if (colunasKeyEscondidas.includes(keyColumn)) {
      novasColunasKeyEscondidas = colunasKeyEscondidas.filter((v) => v !== keyColumn);
      setColunasKeyEscondidas(novasColunasKeyEscondidas);
      setValorInputColuna((prev) => {
        const novosValores = { ...prev, [keyColumn]: null };
        textosInputs = novosValores;
        return novosValores;
      });
    } else {
      novasColunasKeyEscondidas = colunasKeyEscondidas.concat(keyColumn);
      setColunasKeyEscondidas(novasColunasKeyEscondidas);
      excluirColunasEscondidas();
    }

    try {
      if (variante?.esconderColuna && dadosTabela.colunas?.length) {
        const chave = gerarChaveLocalStorage();
        const configuracao = {
          colunasVisiveis: novasColunasVisiveis,
          colunasEscondidas: novasColunasEscondidas,
          colunasKeyEscondidas: novasColunasKeyEscondidas,
          timestamp: Date.now(),
          versao: "1.0",
        };
        localStorage.setItem(chave, JSON.stringify(configuracao));
      }
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
    }
  };

  function habilitarPesquisaColuna() {
    if (variante.filtroColuna || variante === "completa") {
      const novoStatus = !statusPesquisaColuna;
      setStatusPesquisaColuna(novoStatus);
      salvarFiltroColunasStorage(textosInputs, novoStatus);
    }
  }

  const pegarValorFiltroGeralDebounced = useCallback(
    debounce((valor) => {
      setValorFiltroGeralDebounced(valor ?? "");
    }, DEBOUNCE_MS),
    [],
  );

  const atualizarFiltroColunasDebounced = useCallback(
    debounce(() => {
      setValorFiltroColunasDebounced({ ...filtrosColunasRef.current });
    }, DEBOUNCE_MS),
    [],
  );

  useEffect(() => {
    if (!inicializacaoCompleta) return;

    if (variante?.filtroGeral || variante === "completa") {
      pegarValorFiltroGeralDebounced(valorInputGeral);
    }
  }, [valorInputGeral, pegarValorFiltroGeralDebounced, variante?.filtroGeral]);

  function trocarPaginaInput(pagina) {
    const paginaFormatada = pagina - 1;
    mudarPaginaTabela(paginaFormatada);
  }

  useEffect(() => {
    if (variante?.mudarPaginaCashback) {
      pagination.fns.onSetPage(dadosTabela?.pagina);
    }
    pagination.fns.onSetPage(dadosTabela?.pagina);
  }, [dadosTabela?.pagina]);

  function trocarQuantidadeItensTabela(valor) {
    setQuantidadeItens(valor);
    pagination.fns.onSetPage(0);
    quantidadePaginacao = valor;

    if (dadosTabela?.mudarTamanhoPagina) {
      dadosTabela.mudarTamanhoPagina(valor);
    }
  }

  useEffect(() => {
    if (variante?.footerMonetarioAnual) {
      setIds([dadosTabela?.nodes[dadosTabela?.nodes?.length - 1]?.id]);
    }
  }, [dadosTabela?.nodes]);

  useEffect(() => {
    if (variante?.mudarPaginacaoCheckbox) {
      pagination.fns.onSetPage(dadosTabela?.pagina);
    }
  }, [dadosTabela?.pagina]);

  const select = useRowSelect(
    dadosTabela,
    {
      state: {
        ids: dadosTabela?.idsCheckboxSelecionados
          ? dadosTabela?.idsCheckboxSelecionados?.map((item) => Number(item))
          : [],
      },
      onChange: onSelectChange,
    },
    {
      rowSelect: variante?.checkboxSelecionarApenasUm
        ? SelectTypes.SingleSelect
        : SelectTypes.MultiSelect,
      buttonSelect: variante?.checkboxSelecionarApenasUm
        ? SelectTypes.SingleSelect
        : SelectTypes.MultiSelect,
      clickType: variante?.checkboxCliqueLinha
        ? SelectClickTypes.RowClick
        : SelectClickTypes.ButtonClick,
    },
  );

  const [conteudoInputsSelecionados, setConteudoInputsSelecionados] = useState([]);

  const obterIdsSelecionados = (state) => {
    if (variante?.checkboxSelecionarApenasUm) {
      return state.id || state.id === 0 ? [state.id] : [];
    }

    return state.ids || [];
  };

  function onSelectChange(action, state) {
    const idsSelecionados = obterIdsSelecionados(state);
    const linhasSelecionadas = dadosTabela.nodes.filter((item) =>
      idsSelecionados.includes(item.id),
    );

    setConteudoInputsSelecionados(linhasSelecionadas);

    if (typeof variante?.onSelecionarCheckbox === "function") {
      variante.onSelecionarCheckbox({
        action,
        state,
        idsSelecionados,
        linhasSelecionadas,
        nodes: dadosTabela.nodes,
      });
    }
  }

  const expandirGridRepeat = (gridString) => {
    const grid = gridString.replace(/!important/g, "").trim();

    try {
      let resultado = "";
      for (let i = 0; i < grid.length; i++) {
        if (grid.startsWith("repeat(", i)) {
          i += 7;

          let countStr = "";
          while (/\d/.test(grid[i])) {
            countStr += grid[i++];
          }

          if (grid[i] === ",") i++;
          while (/\s/.test(grid[i])) i++;

          let depth = 1;
          let value = "";

          while (i < grid.length && depth > 0) {
            if (grid[i] === "(") depth++;
            else if (grid[i] === ")") depth--;
            if (depth > 0) value += grid[i];
            i++;
          }

          const count = parseInt(countStr, 10);
          const expandido = Array(count).fill(value.trim()).join(" ");

          resultado += expandido;
          i--;
        } else {
          resultado += grid[i];
        }
      }

      const gridExpandido = resultado.trim().replace(/\s+/g, " ");

      return gridExpandido;
    } catch (error) {
      console.error("Falha ao expandir grid:", error);
      return grid;
    }
  };

  function dividirLargurasRespeitandoParenteses(gridString) {
    const partes = [];
    let atual = "";
    let profundidade = 0;

    for (let i = 0; i < gridString.length; i++) {
      const char = gridString[i];

      if (char === "(") {
        profundidade++;
        atual += char;
      } else if (char === ")") {
        profundidade--;
        atual += char;
      } else if (/\s/.test(char) && profundidade === 0) {
        if (atual.trim()) {
          partes.push(atual.trim());
          atual = "";
        }
      } else {
        atual += char;
      }
    }

    if (atual.trim()) partes.push(atual.trim());
    return partes;
  }

  const calcularGridColuna = () => {
    if (!dadosTabela.gridColuna) {
      const fallback = `repeat(${
        colunasVisiveis?.length || dadosTabela.colunas?.length
      }, minmax(150px, 1fr))`;
      console.log("Sem gridColuna definido, usando fallback:", fallback);
      return fallback;
    }

    if (!colunasEscondidas || colunasEscondidas.length === 0) {
      return dadosTabela.gridColuna;
    }

    try {
      const gridExpandido = expandirGridRepeat(dadosTabela.gridColuna);

      const larguras = dividirLargurasRespeitandoParenteses(gridExpandido);

      if (larguras.length !== dadosTabela.colunas.length) {
        console.error("Mismatch: larguras.length !== colunas.length", {
          larguras: larguras.length,
          colunas: dadosTabela.colunas.length,
        });
        return `repeat(${colunasVisiveis.length}, minmax(150px, 1fr))`;
      }

      const mapeamento = dadosTabela.colunas.map((coluna, index) => ({
        index,
        titulo: coluna.titulo,
        largura: larguras[index],
        escondida: colunasEscondidas.includes(coluna.titulo),
      }));

      const largurasFiltradas = mapeamento
        .filter((item) => !item.escondida)
        .map((item) => item.largura);

      const gridRecalculado = largurasFiltradas.join(" ");
      const resultado = gridRecalculado + " !important";

      return resultado;
    } catch (error) {
      console.error("Erro ao recalcular grid de colunas:", error);
      return `repeat(${colunasVisiveis.length}, minmax(150px, 1fr))`;
    }
  };

  const temaTabela = useTheme(
    {
      Table: `
    --data-table-library_grid-template-columns: ${calcularGridColuna()}
    `,
    },
    [colunasVisiveis, dadosTabela.gridColuna, dadosTabela.colunas?.length, colunasEscondidas],
  );

  const temaTabelaAgrupada = useTheme({
    Table: `
    --data-table-library_grid-template-columns: ${
      dadosTabela.gridColunaAgrupada
        ? dadosTabela?.gridColunaAgrupada
        : `repeat(${dadosTabela.colunasAgrupadas?.length}, minmax(120px, 1fr))`
    }
    `,
  });

  const mostrarBordasColunasAgrupadas = !variante?.semBordasColunasAgrupadas;

  const renderizarTituloColunaAgrupada = (coluna) => {
    const filhos = coluna?.filhos?.filter(Boolean) ?? [];

    if (!filhos.length) return coluna.titulo;

    const obterPesoGrupo = (grupo) => {
      if (Number.isInteger(grupo?.inicio) && Number.isInteger(grupo?.fim)) {
        return Math.max(grupo.fim - grupo.inicio + 1, 1);
      }

      const filhosGrupo = grupo?.filhos?.filter(Boolean) ?? [];
      return filhosGrupo.length
        ? filhosGrupo.reduce((total, filho) => total + obterPesoGrupo(filho), 0)
        : 1;
    };

    const renderizarGrupo = (grupo) => {
      const filhosGrupo = grupo?.filhos?.filter(Boolean) ?? [];

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            backgroundColor: grupo.backgroundColor,
          }}
        >
          <div
            style={{
              padding: "8px 12px",
              textAlign: grupo.textAlign ?? "center",
            }}
          >
            {grupo.titulo}
          </div>

          {filhosGrupo.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: filhosGrupo
                  .map((filho) => `${obterPesoGrupo(filho)}fr`)
                  .join(" "),
                flex: 1,
                borderTop: "1px solid rgba(0,0,0,0.2)",
              }}
            >
              {filhosGrupo.map((filho, index) => (
                <div
                  key={filho.key ?? `${filho.titulo}-${index}`}
                  style={{
                    display: "flex",
                    minWidth: 0,
                    borderLeft: index > 0 ? "1px solid rgba(0,0,0,0.2)" : undefined,
                  }}
                >
                  {renderizarGrupo(filho)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    };

    return renderizarGrupo(coluna);
  };

  const estiloAgrupado = (index) => {
    const estilo = {};
    dadosTabela.colunasAgrupadas &&
      dadosTabela.colunasAgrupadas.forEach((agrupamento) => {
        if (agrupamento.inicio <= index && agrupamento.fim >= index)
          estilo["backgroundColor"] = agrupamento.backgroundColor;
        if (mostrarBordasColunasAgrupadas && agrupamento.inicio === index)
          estilo["borderLeft"] = `3px solid rgba(0,0,0,0.2)`;
      });

    return estilo;
  };

  const estiloCelulaAgrupada = (index) => {
    return {
      borderLeft:
        mostrarBordasColunasAgrupadas &&
        dadosTabela.colunasAgrupadas?.find((item) => item.inicio === index)
          ? "3px solid rgba(0,0,0,0.2)"
          : undefined,
    };
  };

  const [ids, setIds] = useState([]);

  const handleExpand = (item) => {
    if (variante?.footerMonetarioAnual) {
      return;
    }

    if (variante?.selecaoUnicoItem) {
      if (ids.includes(item.id)) {
        setIds([]);
        return;
      } else {
        setIds([item.id]);
        return;
      }
    }

    if (dadosTabela.itensTabelaSecundaria)
      if (!dadosTabela.itensTabelaSecundaria) {
        return;
      }

    if (ids.includes(item.id)) {
      setIds(ids.filter((id) => id !== item.id));
    } else if (item.listaResgates !== null) {
      setIds(ids.concat(item.id));
    }
  };

  useEffect(() => {
    if (variante?.footerMonetarioAnual) {
      setIds([dadosTabela?.nodes[dadosTabela?.nodes?.length - 1]?.id]);
    }
  }, [dadosTabela?.nodes]);

  function resetarCamposFiltroNull() {
    const novosTextosInputs = colunasVisiveis
      .filter((nomeColuna) => nomeColuna !== "Ações")
      .reduce((obj, coluna) => {
        const colunaConfig = dadosTabela.colunas?.find((c) => c.key === coluna);
        obj[coluna] =
          colunaConfig?.valorPadrao || (colunaConfig?.defaultStatusAtivo ? "ATIVO" : null);
        return obj;
      }, {});

    setValorInputColuna(novosTextosInputs);
    filtrosColunasRef.current = novosTextosInputs;
    textosInputs = novosTextosInputs;

    atualizarFiltroColunasDebounced.cancel();
    setValorFiltroColunasDebounced(novosTextosInputs);

    if (variante?.filtroColuna || variante === "completa") {
      salvarFiltroColunasStorage(novosTextosInputs, statusPesquisaColuna);
    }

    return novosTextosInputs;
  }

  function excluirColunasEscondidas() {
    if (Array.isArray(colunasKeyEscondidas) && colunasKeyEscondidas.length > 0) {
      const novosValoresRef = { ...filtrosColunasRef.current };
      colunasKeyEscondidas.forEach((coluna) => {
        delete novosValoresRef[coluna];
      });
      filtrosColunasRef.current = novosValoresRef;
    }

    setValorInputColuna((prev) => {
      if (!prev || typeof prev !== "object") {
        return prev;
      }

      if (!Array.isArray(colunasKeyEscondidas) || colunasKeyEscondidas.length === 0) {
        return prev;
      }

      const novosValores = { ...prev };
      colunasKeyEscondidas.forEach((coluna) => {
        delete novosValores[coluna];
      });

      textosInputs = novosValores;
      return novosValores;
    });
  }

  function pegarValorFiltroColuna(textoColuna, tituloColuna, dispararBusca = true) {
    excluirColunasEscondidas();

    if (!tituloColuna) {
      atualizarFiltroColunasDebounced.cancel();
      setValorFiltroColunasDebounced({ ...filtrosColunasRef.current });
      return;
    }

    const valorNormalizado = textoColuna || null;
    const valorAtual = filtrosColunasRef.current?.[tituloColuna] ?? null;

    if (Object.is(valorAtual, valorNormalizado)) {
      return;
    }

    setValorInputColuna((prev) => {
      const novosValores = { ...prev, [tituloColuna]: valorNormalizado };
      filtrosColunasRef.current = novosValores;
      textosInputs = novosValores;

      if (variante?.filtroColuna || variante === "completa") {
        salvarFiltroColunasStorage(novosValores, statusPesquisaColuna);
      }

      return novosValores;
    });

    if (dispararBusca) {
      atualizarFiltroColunasDebounced();
    } else {
      atualizarFiltroColunasDebounced.cancel();
    }
  }

  function pegarValorFiltroColunaData(coluna, valor, contexto) {
    if (variante?.desativarBuscaComDataIncompleta && contexto?.validationError) {
      return;
    }

    pegarValorFiltroColuna(valor, coluna.key);
  }

  useEffect(() => {
    if (!inicializacaoCompleta) return;

    excluirColunasEscondidas();
    atualizarFiltroColunasDebounced.cancel();

    setValorFiltroColunasDebounced((prev) => {
      const novoValor = { ...filtrosColunasRef.current };
      if (JSON.stringify(prev) === JSON.stringify(novoValor)) return prev;
      return novoValor;
    });
  }, [colunasKeyEscondidas]);

  useEffect(() => {
    // Configurar quantidade de itens sem resetar filtros (filtros são carregados em outros useEffects)
    if (itemUnico) {
      setQuantidadeItensMostrarTabela(["1"]);
      return;
    }

    const opcoesCustomizadas = variante?.opcoesItensPorPagina;
    setQuantidadeItensMostrarTabela(
      Array.isArray(opcoesCustomizadas) && opcoesCustomizadas.length > 0
        ? opcoesCustomizadas.map(String)
        : ["10", "25", "50", "75", "100"],
    );
  }, []);

  useEffect(() => {
    return () => {
      quantidadePaginacao = null;
      setQuantidadeItens(
        itemUnico
          ? "1"
          : variante?.tamanhoPaginaInicial
            ? String(variante.tamanhoPaginaInicial)
            : quantidadePaginacao !== null
              ? quantidadePaginacao
              : "10",
      );
    };
  }, []);

  function blurTabela(timer = 800) {
    setPrimeiroCarregamento(true);
    document.getElementsByClassName("containerTabelaLib")[0].classList.add("blurTabela");

    setTimeout(() => {
      try {
        document.getElementsByClassName("containerTabelaLib")[0].classList.remove("blurTabela");
        setPrimeiroCarregamento(false);
      } catch (e) {}
    }, timer);
  }

  function formatarData(data) {
    if (!data) return null;
    if (typeof data === "object") {
      return dayjs(data).format("YYYY-MM-DD");
    }
    const partes = data.split("/");

    const dataFormatada = partes[2] + "-" + partes[1] + "-" + partes[0];

    return dataFormatada;
  }
  const body = {
    data: valorFiltroColunasDebounced?.data ? formatarData(valorFiltroColunasDebounced.data) : null,
    diaDaSemana: mudarValoresSelect(diaDaSemana, itensDiasDaSemana),
    ordenacao: ordenacaoColunas,
    conteudoBody: {
      palavraChave: valorFiltroGeralDebounced,
      palavraChaveEstado: valorFiltroGeralDebounced,
      colunasValores: valorFiltroColunasDebounced,
      valoresSelect: valorFiltroColunasDebounced,
    },
    valoresInput: conteudoInputsSelecionados,
    linhasPorPagina: quantidadeItens,
    colunasVisiveisKey: colunasVisiveis,
  };

  const possuiSelecaoTodosRegistrosCustomizada =
    typeof variante?.onSelecionarTodosRegistros === "function";

  const todosRegistrosSelecionados = possuiSelecaoTodosRegistrosCustomizada
    ? Boolean(variante?.todosRegistrosSelecionados)
    : select.state.all;

  const selecaoParcialRegistros = possuiSelecaoTodosRegistrosCustomizada
    ? Boolean(variante?.selecaoParcialRegistros)
    : !select.state.all && !select.state.none;

  const alternarSelecaoTodosRegistros = () => {
    if (possuiSelecaoTodosRegistrosCustomizada) {
      variante.onSelecionarTodosRegistros({
        selecionar: !todosRegistrosSelecionados,
        dadosFiltro: body,
      });
      return;
    }

    select.fns.onToggleAll();
  };

  function obterScrollsParaPreservar() {
    const scrolls = [];
    const root = refTabelaScroll.current;

    if (!root) return scrolls;

    const elementosTabela = [...root.querySelectorAll("thead, tbody, tfoot")].filter(
      (elemento) =>
        elemento.scrollTop > 0 ||
        elemento.scrollLeft > 0 ||
        elemento.scrollHeight > elemento.clientHeight ||
        elemento.scrollWidth > elemento.clientWidth,
    );

    elementosTabela.forEach((elemento) => {
      scrolls.push({
        elemento,
        top: elemento.scrollTop,
        left: elemento.scrollLeft,
      });
    });

    let elemento = root.parentElement;

    while (elemento && elemento !== document.body && elemento !== document.documentElement) {
      const estilo = window.getComputedStyle(elemento);
      const overflow = `${estilo.overflow} ${estilo.overflowY} ${estilo.overflowX}`;
      const podeRolar = /(auto|scroll|overlay)/.test(overflow);
      const temScroll =
        elemento.scrollHeight > elemento.clientHeight ||
        elemento.scrollWidth > elemento.clientWidth ||
        elemento.scrollTop > 0 ||
        elemento.scrollLeft > 0;

      if (podeRolar && temScroll) {
        scrolls.push({
          elemento,
          top: elemento.scrollTop,
          left: elemento.scrollLeft,
        });
      }

      elemento = elemento.parentElement;
    }

    return scrolls;
  }

  function salvarPosicaoScrollPaginacao(paginaDestino) {
    posicaoScrollPaginacaoRef.current = {
      paginaDestino,
      windowX: window.scrollX || window.pageXOffset || 0,
      windowY: window.scrollY || window.pageYOffset || 0,
      scrolls: obterScrollsParaPreservar(),
    };
  }

  function restaurarPosicaoScrollPaginacao() {
    const posicao = posicaoScrollPaginacaoRef.current;
    if (!posicao) return;

    posicao.scrolls.forEach(({ elemento, top, left }) => {
      if (!elemento?.isConnected) return;

      elemento.scrollTop = top;
      elemento.scrollLeft = left;
    });

    window.scrollTo(posicao.windowX, posicao.windowY);
  }

  function executarNoProximoFrame(callback) {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(callback);
      return;
    }

    setTimeout(callback, 0);
  }

  function agendarRestauracaoScrollPaginacao(limparDepois = false) {
    executarNoProximoFrame(() => {
      restaurarPosicaoScrollPaginacao();
      executarNoProximoFrame(() => {
        restaurarPosicaoScrollPaginacao();

        if (limparDepois) {
          posicaoScrollPaginacaoRef.current = null;
        }
      });
    });
  }

  function mudarPaginaTabela(pagina) {
    salvarPosicaoScrollPaginacao(pagina);
    pagination.fns.onSetPage(pagina);
    dadosTabela?.mudarPagina(pagina, body);
    agendarRestauracaoScrollPaginacao();
  }

  useEffect(() => {
    const posicao = posicaoScrollPaginacaoRef.current;
    if (!posicao) return;

    agendarRestauracaoScrollPaginacao(dadosTabela?.pagina === posicao.paginaDestino);
  }, [dadosTabela?.pagina, dadosTabela?.nodes]);

  useEffect(() => {
    if (!inicializacaoCompleta) return;

    if (primeiraExecucaoValoresColunaRef.current) {
      primeiraExecucaoValoresColunaRef.current = false;

      if (variante?.desativarBuscaInternaInicial || variante?.funcaoBuscaInicial) {
        return;
      }
    }

    if (Object.values(dadosTabela)?.length > 0) {
      if (dadosTabela?.valoresColuna) {
        dadosTabela?.valoresColuna(body);
      }
    }
  }, [
    valorFiltroColunasDebounced,
    ordenacaoColunas,
    valorFiltroGeralDebounced,
    quantidadeItens,
    diaDaSemana,
    inicializacaoCompleta,
  ]);

  useEffect(() => {
    if (!inicializacaoCompleta) return;
    if (efeitoCheckboxMontou.current) {
      efeitoCheckboxMontou.current = false;
      if (!conteudoInputsSelecionados?.length) {
        return;
      }
    }

    if (Object.values(dadosTabela)?.length > 0) {
      if (dadosTabela?.valoresColuna && typeof variante?.onSelecionarCheckbox !== "function") {
        dadosTabela?.valoresColuna(body, "conteudoInputsSelecionados");
      }
    }
  }, [conteudoInputsSelecionados]);

  useEffect(() => {
    if (variante?.tabelaSecundariaSobDemanda && dadosTabela?.buscarSobDemanda)
      dadosTabela?.buscarSobDemanda(ids, dadosTabela);
  }, [ids]);

  const filtragemTabelaMemo = useMemo(() => {
    if (!(
      variante?.containerFiltroGeral ||
      variante === "completa" ||
      variante?.filtroColunaVisivel ||
      variante?.filtroColuna
    )) {
      return null;
    }

    return (
      <S.FiltragemTabela
        style={{
          justifyContent: variante.filtroGeral === false || undefined ? "end" : "space-between",
          flexDirection:
            largura < 1068 && !variante.periodoPersonalizado
              ? "column"
              : largura < 1100 && variante.periodoPersonalizado
                ? "column"
                : "row",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 32,
            flexDirection:
              largura > 1456 && variante.periodoPersonalizado
                ? "row"
                : largura > 1456 && !variante.periodoPersonalizado
                  ? "row"
                  : largura < 1456 && !variante.periodoPersonalizado
                    ? "row"
                    : "column",
          }}
        >
          {(variante?.filtroGeral || variante === "completa") && (
            <S.TextFieldIcon>
              <CampoDeTexto
                style={{
                  width: largura >= 840 ? 292 : "100%",
                  height: "56px",
                }}
                value={valorInputGeral}
                onChange={(e) => {
                  if (variante?.pesquisarEmpresas) {
                    const value = e.target.value;
                    const finalValue = formatarInscricaoFederal(value);
                    setValorInputGeral(finalValue);
                  } else {
                    setValorInputGeral(e.target.value);
                  }
                }}
                placeholder={
                  variante?.filtroGeralPlaceHolder
                    ? variante?.filtroGeralPlaceHolder
                    : t("placeholders.filterTable")
                }
                sx={{
                  "& .MuiInputBase-root": {
                    padding: "0 5px",
                    margin: 0,
                  },
                  ".MuiSelect-select": {
                    marginLeft: "5px",
                  },
                  ".MuiInputBase-input": {
                    padding: "17px",
                    fontSize: "15px",
                  },
                }}
              />
            </S.TextFieldIcon>
          )}

          {variante?.filtroPeriodicidade && variante?.filtroPeriodicidade}
        </div>

        <S.BotoesIcone
          larguraTotal={
            !variante?.filtroColuna && !variante?.esconderColuna && variante?.pedidoDeCompra
          }
          $gapCompacto={Boolean(variante?.barraIcones)}
        >
          {variante?.botoesExtrasEsquerda && variante?.botoesExtrasEsquerda}
          <S.Botoes $gapCompacto={Boolean(variante?.barraIcones)}>
            {(variante?.filtroColuna ||
              variante === "completa" ||
              variante?.filtroGeral ||
              variante?.esconderColuna) &&
              (variante?.temFiltros !== undefined ? variante.temFiltros : temFiltrosSalvos()) && (
                <BotaoVermelhoVazado
                  onClick={limparFiltrosSalvos}
                  className="btn-limpar-filtros-tabela"
                  texto="Limpar filtros"
                  sublinhado
                />
              )}

            {(variante?.filtroColuna || variante === "completa") &&
              (variante?.barraIcones ? (
                <S.BotaoIconeBarra
                  type="button"
                  aria-label="Pesquisar campos"
                  title="Pesquisar campos"
                  aria-pressed={Boolean(statusPesquisaColuna)}
                  className="btn-pesquisar-tabela"
                  onClick={() => {
                    resetarCamposFiltroNull();
                    habilitarPesquisaColuna();
                  }}
                >
                  <FilterListRoundedIcon />
                </S.BotaoIconeBarra>
              ) : (
                <BotaoAzulVazado
                  onClick={() => {
                    resetarCamposFiltroNull();
                    habilitarPesquisaColuna();
                  }}
                  className="btn-pesquisar-tabela"
                  texto="Pesquisar campos"
                  sublinhado
                />
              ))}

            {(variante?.esconderColuna || variante === "completa") && (
              <S.StyledPopupColunas
                trigger={() =>
                  variante?.barraIcones ? (
                    <S.BotaoIconeBarra type="button" aria-label="Colunas" title="Colunas">
                      <ViewColumnRoundedIcon />
                    </S.BotaoIconeBarra>
                  ) : (
                    <S.BotaoAzulRefatorado>
                      <S.IconeColunas weight="fill" />
                      Colunas
                    </S.BotaoAzulRefatorado>
                  )
                }
                position="bottom center"
                closeOnDocumentClick
              >
                <S.ContainerCheckbox>
                  {dadosTabela?.colunas?.map((coluna, index) => {
                    // rotuloLista: nome na lista para coluna de título em branco (ex.: só ícone).
                    const rotulo = coluna?.rotuloLista ?? coluna?.titulo;
                    if (!rotulo?.trim()) return null;
                    return (
                      <Fragment key={`${coluna.titulo}-${index}`}>
                        <CheckBox
                          desabilitado={coluna.esconder === false || primeiroCarregamento}
                          corTexto={tema.cores.textoPopup}
                          texto={rotulo}
                          onClick={() => {
                            blurTabela(2500);
                            toggleColumn(coluna.titulo, coluna.key);
                          }}
                          tamanho="SM"
                          valor={!colunasEscondidas.includes(coluna.titulo)}
                          checked={!colunasEscondidas.includes(coluna.titulo)}
                          id={index}
                        />
                        <S.StyledHr />
                      </Fragment>
                    );
                  })}
                </S.ContainerCheckbox>
              </S.StyledPopupColunas>
            )}
          </S.Botoes>

          {variante?.botoesExtrasDireita && variante?.botoesExtrasDireita}
        </S.BotoesIcone>
      </S.FiltragemTabela>
    );
  }, [
    variante,
    largura,
    valorInputGeral,
    dadosTabela?.colunas,
    colunasEscondidas,
    primeiroCarregamento,
    tema,
    temFiltrosSalvos,
    limparFiltrosSalvos,
    resetarCamposFiltroNull,
    habilitarPesquisaColuna,
    blurTabela,
    toggleColumn,
  ]);

  const quantidadeMinimaItensScroll =
    [variante?.quantidadeMinimaItensScroll, dadosTabela?.linhasPorPagina, quantidadeItens].reduce(
      (quantidadeValida, quantidadeAtual) => {
        if (quantidadeValida) return quantidadeValida;

        const quantidadeNumerica = Number(quantidadeAtual);
        return Number.isFinite(quantidadeNumerica) && quantidadeNumerica > 0
          ? Math.trunc(quantidadeNumerica)
          : null;
      },
      null,
    ) || 10;
  const deveFixarAlturaTabela = dadosTabela.nodes?.length > quantidadeMinimaItensScroll;
  const usarAlturaContainerCustomizada = Boolean(alturaContainerTabela);
  const deveFixarFooter = fixedFooter && usarAlturaContainerCustomizada;
  const alturaDimensionadaTabela =
    (deveFixarAlturaTabela || deveFixarFooter) && usarAlturaContainerCustomizada
      ? alturaContainerTabela
      : "auto";
  const maxAlturaCorpoTabela =
    deveFixarAlturaTabela && !usarAlturaContainerCustomizada ? alturaCorpoTabelaFixa : null;
  const textoNenhumResultado = variante?.textoNenhumResultado
    ? variante.textoNenhumResultado
    : variante?.carteiraDigital
      ? "Desculpe, não foi possível encontrar nenhuma carteira digital vinculada."
      : variante?.vinculoEstacao
        ? "Desculpe, não foi possível encontrar nenhuma estação vinculada."
        : variante?.pager
          ? "Desculpe, não foi possível encontrar nenhum Pager vinculado."
          : variante?.geradorEtiqueta
            ? "Nenhum item selecionado."
            : "Desculpe, nenhum resultado encontrado.";
  const manterEstruturaTabelaSemResultados =
    variante?.preservarScrollHorizontal &&
    variante?.scrollHorizontalAbaixoFooter &&
    !dadosTabela.nodes?.length;
  const possuiPaginas = Number(dadosTabela.paginas) > 0;

  useLayoutEffect(() => {
    if (!deveFixarAlturaTabela || usarAlturaContainerCustomizada) {
      setAlturaCorpoTabelaFixa(null);
      return;
    }

    const root = refTabelaScroll.current;
    const tbodies = Array.from(root?.querySelectorAll("tbody") || []);
    const tbodyPrincipal = tbodies[tbodies.length - 1];

    if (!tbodyPrincipal) return;

    const obterLinhasPrincipais = () =>
      Array.from(tbodyPrincipal.children || []).filter((elemento) => {
        const tag = elemento.tagName?.toLowerCase();
        const role = elemento.getAttribute("role");
        const className = typeof elemento.className === "string" ? elemento.className : "";

        return (
          tag === "tr" || role === "row" || className.includes("trow") || className.includes("Row")
        );
      });

    const calcularAlturaCorpo = () => {
      const linhas = obterLinhasPrincipais().slice(0, quantidadeMinimaItensScroll);
      const alturaLinhas = linhas.reduce(
        (total, linha) => total + linha.getBoundingClientRect().height,
        0,
      );
      const novaAltura = alturaLinhas > 0 ? `${Math.ceil(alturaLinhas)}px` : null;

      setAlturaCorpoTabelaFixa((alturaAtual) =>
        alturaAtual === novaAltura ? alturaAtual : novaAltura,
      );
    };

    calcularAlturaCorpo();

    if (typeof ResizeObserver !== "function") return;

    const resizeObserver = new ResizeObserver(calcularAlturaCorpo);
    obterLinhasPrincipais()
      .slice(0, quantidadeMinimaItensScroll)
      .forEach((linha) => resizeObserver.observe(linha));

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    carregandoTabela,
    colunasEscondidas,
    colunasVisiveis,
    dadosTabela.nodes,
    deveFixarAlturaTabela,
    largura,
    quantidadeMinimaItensScroll,
    statusPesquisaColuna,
    usarAlturaContainerCustomizada,
  ]);

  return (
    <S.StyledContainerGlobal
      key={key}
      hoverLinhas={variante?.hoverLinhas}
      layoutFlexivel={layoutFlexivel}
      variantNcmCest={variantNcmCest}
      estacaoImpressao={estacaoImpressao}
    >
      {filtragemTabelaMemo}

      <S.ContainerTabelaWrapper
        height={alturaDimensionadaTabela}
        minHeight={alturaDimensionadaTabela}
        fixedFooter={fixedFooter}
        className="containerExteriorTabelaLib"
      >
        <S.ContainerTabela
          ref={refTabelaScroll}
          className="containerTabelaLib"
          headerSecundario={headerSecundario}
          maxAlturaCorpoTabela={maxAlturaCorpoTabela}
          scrollHorizontalAbaixoFooter={variante?.scrollHorizontalAbaixoFooter}
        >
          {((primeiroCarregamento && variante?.esconderColuna) || variante?.loadingBodyTabela) && (
            <Carregando />
          )}
          {!variante?.loadingBodyTabela && headerSecundario && (
            <Table
              ref={refTabelaAgupadaScroll}
              data={dadosTabela}
              sort={sort}
              theme={temaTabelaAgrupada}
              layout={{
                custom: true,
                fixedHeader: true,
                horizontalScroll: true,
              }}
              tree={dadosTabela?.treeTabela && tree}
              select={dadosTabela.colunas[0]?.key === "checkbox" && select}
            >
              {(tableList) => (
                <>
                  <Header>
                    <HeaderRow>
                      {dadosTabela &&
                        dadosTabela.colunasAgrupadas &&
                        dadosTabela.colunasAgrupadas
                          .filter((coluna) => !colunasEscondidas.includes(coluna.titulo))
                          .map((coluna, index) => {
                            return (
                              <Fragment key={index + coluna.key}>
                                <HeaderCell
                                  key={coluna.key + index}
                                  style={{
                                    backgroundColor: coluna.backgroundColor,
                                    fontWeight: "bold",
                                    alignItems: coluna.filhos?.length ? "stretch" : "flex-start",
                                    textAlign: coluna.textAlign,
                                    padding: coluna.filhos?.length ? 0 : undefined,
                                    ...(mostrarBordasColunasAgrupadas
                                      ? {
                                          borderTop: "3px solid rgba(0,0,0,0.2)",
                                          borderBottom: "3px solid rgba(0,0,0,0.2)",
                                          borderLeft: coluna.inicio
                                            ? "3px solid rgba(0,0,0,0.2)"
                                            : "none",
                                        }
                                      : {}),
                                  }}
                                  resize={coluna.resize}
                                >
                                  {renderizarTituloColunaAgrupada(coluna)}
                                </HeaderCell>
                              </Fragment>
                            );
                          })}
                    </HeaderRow>
                  </Header>
                </>
              )}
            </Table>
          )}
          {!variante?.loadingBodyTabela && (
            <Table
              data={dadosTabela}
              sort={sort}
              theme={temaTabela}
              layout={{
                custom: true,
                fixedHeader: true,
                horizontalScroll: true,
              }}
              tree={(variante === "accordionTabela" || variante?.treeTabela) && tree}
              pagination={pagination}
              select={dadosTabela.colunas[0]?.key === "checkbox" && select}
            >
              {(tableList) => (
                <>
                  <Header>
                    <HeaderRow>
                      {dadosTabela &&
                        dadosTabela?.colunas
                          .map((coluna, indexOriginal) => ({
                            coluna,
                            indexOriginal,
                          }))
                          .filter(({ coluna }) => !colunasEscondidas.includes(coluna.titulo))
                          .map(({ coluna, indexOriginal }, index) => {
                            const statusOrdenavel =
                              coluna.selectEstiloStatus &&
                              !statusPesquisaColuna &&
                              coluna.ordenacao !== false &&
                              variante?.ordenacao !== false;
                            const CelulaCabecalhoStatus = statusOrdenavel
                              ? HeaderCellSort
                              : HeaderCell;
                            return (
                              <Fragment key={coluna + index + "key2"}>
                                {coluna.key === "checkbox" &&
                                !variante?.checkboxSelecionarApenasUm ? (
                                  <HeaderCell
                                    key={coluna.key + index}
                                    style={{ alignItems: "flex-start" }}
                                  >
                                    <CheckBox
                                      tamanho={"MD"}
                                      valor={todosRegistrosSelecionados}
                                      onClick={alternarSelecaoTodosRegistros}
                                      indeterminate={selecaoParcialRegistros}
                                    />
                                  </HeaderCell>
                                ) : coluna.key === "ativo" ||
                                  coluna.selectEstiloStatus ||
                                  (coluna.key === "status" &&
                                    !variante?.consultasRapidas &&
                                    (coluna.ordenacao !== true || statusPesquisaColuna)) ? (
                                  <CelulaCabecalhoStatus
                                    key={coluna.key + index}
                                    {...(statusOrdenavel && {
                                      sortKey: coluna.sortKey || coluna.key,
                                    })}
                                    style={{
                                      alignItems: variante?.statusAlinhado ? "none" : "stretch",
                                    }}
                                  >
                                    {statusPesquisaColuna ? (
                                      <S.ContainerColunaComFiltro>
                                        <S.TituloColuna $semQuebra={coluna.semQuebra}>
                                          {coluna.titulo}
                                        </S.TituloColuna>
                                        <S.CampoFiltroColuna>
                                          {(variante === "completa" ||
                                            variante?.filtroColuna ||
                                            variante?.filtroColunaVisivel) &&
                                            statusPesquisaColuna &&
                                            coluna?.campoDeTexto !== false && (
                                              <S.StyledTextFieldSelect
                                                select
                                                variant="standard"
                                                value={
                                                  valorInputColuna?.[coluna.key] ||
                                                  coluna.valorPadrao ||
                                                  (coluna.defaultStatusAtivo ? "ATIVO" : "TODOS")
                                                }
                                                onChange={(e) => {
                                                  valorSelectStatus(
                                                    e.target.value,
                                                    coluna.key,
                                                    coluna.manterTodosNoFiltro,
                                                  );
                                                }}
                                                sx={{
                                                  "& .MuiInputBase-root": {
                                                    padding: "0 5px",
                                                    margin: 0,
                                                  },
                                                  ".MuiSelect-select": {
                                                    marginLeft: "5px",
                                                    padding: "17px 12px!important",
                                                  },
                                                  ".MuiInputBase-input": {
                                                    padding: "17px",
                                                    fontSize: "15px",
                                                  },
                                                }}
                                              >
                                                {(coluna.selectEstiloStatus && coluna.itemsSelecao
                                                  ? coluna.itemsSelecao.map((item) => ({
                                                      value: item.valor,
                                                      label: item.titulo,
                                                    }))
                                                  : opcoesSelectStatus
                                                ).map((option) => (
                                                  <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                  </MenuItem>
                                                ))}
                                              </S.StyledTextFieldSelect>
                                            )}
                                        </S.CampoFiltroColuna>
                                      </S.ContainerColunaComFiltro>
                                    ) : (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "flex-start",
                                          height: "100%",
                                        }}
                                      >
                                        {coluna.titulo}
                                      </div>
                                    )}
                                  </CelulaCabecalhoStatus>
                                ) : statusPesquisaColuna &&
                                  coluna.titulo &&
                                  String(coluna?.titulo)?.trim() ? (
                                  <HeaderCell
                                    key={coluna.key + index}
                                    resize={coluna.resize}
                                    style={estiloAgrupado(indexOriginal)}
                                  >
                                    <S.ContainerColunaComFiltro>
                                      <S.TituloColuna $semQuebra={coluna.semQuebra}>
                                        {coluna.titulo}
                                      </S.TituloColuna>
                                      <S.CampoFiltroColuna>
                                        {variante?.diaDaSemana && coluna.diaDaSemana && (
                                          <CampoDeSelecao
                                            sx={{
                                              "& .MuiInputBase-root": {
                                                padding: "0 5px",
                                                margin: 0,
                                              },
                                              ".MuiSelect-select": {
                                                marginLeft: "5px",
                                              },
                                              ".MuiInputBase-input": {
                                                padding: "12px",
                                                fontSize: "15px",
                                              },
                                            }}
                                            onChange={(e) => setDiaDaSemana(e.target.value)}
                                            campoVazio={false}
                                            valor={diaDaSemana}
                                            items={itensDiasDaSemana.map(
                                              (item) => item.displayValue,
                                            )}
                                          />
                                        )}
                                        {(variante === "completa" ||
                                          variante?.filtroColuna ||
                                          variante?.filtroColunaVisivel) &&
                                          statusPesquisaColuna &&
                                          !coluna.diaDaSemana &&
                                          coluna?.campoDeTexto !== false && (
                                            <>
                                              {coluna.somenteNumeros ? (
                                                <CampoDeInputNumerico
                                                  sx={{
                                                    "& .MuiInputBase-root": {
                                                      padding: "0 5px",
                                                      margin: 0,
                                                    },
                                                    ".MuiSelect-select": {
                                                      marginLeft: "5px",
                                                    },
                                                    ".MuiInputBase-input": {
                                                      padding: "17px!important",
                                                      fontSize: "15px",
                                                    },
                                                  }}
                                                  fundoTransparente
                                                  placeholder="Digite aqui"
                                                  permitirZeroAEsquerda={
                                                    coluna.numericoDecimal ? false : true
                                                  }
                                                  inverterSeparadores={
                                                    coluna.numericoDecimal === true
                                                  }
                                                  value={valorInputColuna?.[coluna.key]}
                                                  onChange={(valor) =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColuna(
                                                      coluna.key == "cnpj"
                                                        ? formatarInscricaoFederal(valor)
                                                        : valor,
                                                      coluna.key,
                                                    )
                                                  }
                                                />
                                              ) : coluna.autocomplete ? (
                                                <S.StyledAutocomplete
                                                  className={
                                                    coluna.compactarIconesAutocomplete
                                                      ? "autocomplete-icones-compactos"
                                                      : undefined
                                                  }
                                                  disablePortal={
                                                    coluna.travarAutocompleteNoCampo
                                                      ? false
                                                      : !desabilitarPortalAutoComplete
                                                  }
                                                  open={
                                                    coluna.travarAutocompleteNoCampo
                                                      ? autocompleteTravadoAberto === coluna.key
                                                      : undefined
                                                  }
                                                  onOpen={() => {
                                                    if (coluna.travarAutocompleteNoCampo) {
                                                      setAutocompleteTravadoAberto(coluna.key);
                                                    }
                                                  }}
                                                  onClose={() => {
                                                    if (coluna.travarAutocompleteNoCampo) {
                                                      setAutocompleteTravadoAberto(null);
                                                    }
                                                  }}
                                                  value={valorInputColuna?.[coluna.key] || null}
                                                  onChange={(e, value) => {
                                                    coluna.key !== undefined &&
                                                      pegarValorFiltroColuna(value, coluna.key);
                                                  }}
                                                  options={(() => {
                                                    if (
                                                      dadosTabela.dadosAutocomplete &&
                                                      typeof dadosTabela.dadosAutocomplete ===
                                                        "object" &&
                                                      !Array.isArray(dadosTabela.dadosAutocomplete)
                                                    ) {
                                                      const dadosColuna =
                                                        dadosTabela.dadosAutocomplete[coluna.key];
                                                      return dadosColuna?.length
                                                        ? dadosColuna.map((item) => item.descricao)
                                                        : [];
                                                    }
                                                    // Se for um array direto (modo antigo)
                                                    return dadosTabela.dadosAutocomplete?.length
                                                      ? dadosTabela.dadosAutocomplete.map(
                                                          (item) => item.descricao,
                                                        )
                                                      : [];
                                                  })()}
                                                  renderInput={(params) => (
                                                    <S.StyledTextFieldAutocomplete
                                                      {...params}
                                                      placeholder="Digite aqui"
                                                      label=""
                                                    />
                                                  )}
                                                />
                                              ) : coluna.campoDeSelecao ? (
                                                <CampoDeSelecao
                                                  items={
                                                    coluna.itemsSelecao ? coluna.itemsSelecao : []
                                                  }
                                                  referenciaNome={
                                                    coluna.nomeTitulo ? coluna.nomeTitulo : "titulo"
                                                  }
                                                  referenciaValor={
                                                    coluna.nomeValor ? coluna.nomeValor : "valor"
                                                  }
                                                  campoVazio={false}
                                                  valor={
                                                    valorInputColuna?.[coluna.key] ||
                                                    coluna.valorPadrao ||
                                                    null
                                                  }
                                                  limparCampo={
                                                    coluna.permitirLimparSelecao &&
                                                    coluna.key !== undefined &&
                                                    valorInputColuna?.[coluna.key]
                                                      ? () =>
                                                          pegarValorFiltroColuna(null, coluna.key)
                                                      : null
                                                  }
                                                  onChange={(e, value) => {
                                                    coluna.key !== undefined &&
                                                      pegarValorFiltroColuna(
                                                        e.target.value,
                                                        coluna.key,
                                                      );
                                                  }}
                                                />
                                              ) : coluna.campoDeDataHora ? (
                                                <CampoDeData
                                                  titulo=""
                                                  formato="DD/MM/YYYY HH:mm"
                                                  placeholder="DD/MM/AAAA HH:mm"
                                                  value={converterParaDayjsDataHora(
                                                    valorInputColuna?.[coluna.key],
                                                  )}
                                                  limparCampo={() =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColuna(null, coluna.key)
                                                  }
                                                  onChange={(valor, contexto) =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColunaData(
                                                      coluna,
                                                      valor,
                                                      contexto,
                                                    )
                                                  }
                                                  sx={{
                                                    ".MuiInputBase-input": {
                                                      height: "auto",
                                                      fontSize: "15px",
                                                    },
                                                    ".MuiInputAdornment-root": {
                                                      marginRight: "-12px",
                                                    },
                                                    svg: {
                                                      marginLeft: "0px",
                                                    },
                                                  }}
                                                />
                                              ) : coluna.campoDeDataExata ? (
                                                <CampoDeDataSemHora
                                                  titulo="Data"
                                                  value={
                                                    valorInputColuna[coluna.key]
                                                      ? dayjs(valorInputColuna[coluna.key])
                                                      : null
                                                  }
                                                  onChange={(valor) =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColuna(valor, coluna.key)
                                                  }
                                                  onAccept={(valor) =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColuna(valor, coluna.key)
                                                  }
                                                  // Altura fixa + padding do campo zeravam a área do texto e cortavam os dígitos.
                                                  sx={{
                                                    "& .MuiInputBase-root .MuiInputBase-input": {
                                                      height: "auto",
                                                      fontSize: "15px",
                                                    },
                                                    ".MuiInputAdornment-root": {
                                                      marginRight: "-12px",
                                                    },
                                                  }}
                                                />
                                              ) : coluna.campoDeData ? (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 4,
                                                  }}
                                                >
                                                  {comHora === true ? (
                                                    <>
                                                      <CampoDeData
                                                        mostrarHora={comHora}
                                                        titulo={"Data Inicial"}
                                                        sx={{
                                                          ".MuiInputAdornment-root": {
                                                            "margin-right": "-12px",
                                                          },
                                                          svg: {
                                                            "margin-left": "0px",
                                                          },
                                                        }}
                                                        value={
                                                          valorInputColuna[coluna.key]?.dataInicial
                                                            ? dayjs(
                                                                valorInputColuna[coluna.key]
                                                                  ?.dataInicial,
                                                              )
                                                            : null
                                                        }
                                                        onChange={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataInicial: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                        onAccept={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataInicial: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                        error={false}
                                                      />
                                                      <CampoDeData
                                                        mostrarHora={comHora}
                                                        titulo={"Data Final"}
                                                        sx={{
                                                          ".MuiInputAdornment-root": {
                                                            "margin-right": "-12px",
                                                          },
                                                          svg: {
                                                            "margin-left": "0px",
                                                          },
                                                        }}
                                                        value={
                                                          valorInputColuna[coluna.key]?.dataFinal
                                                            ? dayjs(
                                                                valorInputColuna[coluna.key]
                                                                  ?.dataFinal,
                                                              )
                                                            : null
                                                        }
                                                        onChange={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataFinal: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                        onAccept={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataFinal: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                        error={false}
                                                      />
                                                    </>
                                                  ) : (
                                                    <>
                                                      {" "}
                                                      <CampoDeDataSemHora
                                                        titulo={"Data Inicial"}
                                                        sx={{
                                                          ".MuiInputAdornment-root": {
                                                            "margin-right": "-12px",
                                                          },
                                                          svg: {
                                                            "margin-left": "0px",
                                                          },
                                                        }}
                                                        value={
                                                          valorInputColuna[coluna.key]?.dataInicial
                                                            ? dayjs(
                                                                valorInputColuna[coluna.key]
                                                                  ?.dataInicial,
                                                              )
                                                            : null
                                                        }
                                                        onChange={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataInicial: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                        onAccept={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataInicial: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                      />
                                                      <CampoDeDataSemHora
                                                        titulo={"Data Final"}
                                                        sx={{
                                                          ".MuiInputAdornment-root": {
                                                            "margin-right": "-12px",
                                                          },
                                                          svg: {
                                                            "margin-left": "0px",
                                                          },
                                                        }}
                                                        value={
                                                          valorInputColuna[coluna.key]?.dataFinal
                                                            ? dayjs(
                                                                valorInputColuna[coluna.key]
                                                                  ?.dataFinal,
                                                              )
                                                            : null
                                                        }
                                                        onChange={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataFinal: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                        onAccept={(e) =>
                                                          coluna.key !== undefined &&
                                                          pegarValorFiltroColuna(
                                                            {
                                                              ...valorInputColuna[coluna.key],
                                                              dataFinal: e,
                                                            },
                                                            coluna.key,
                                                          )
                                                        }
                                                      />
                                                    </>
                                                  )}
                                                </div>
                                              ) : coluna.campoDeDataInclusao ? (
                                                <CampoDeData
                                                  sx={{
                                                    ".MuiInputBase-input": {
                                                      height: "auto",
                                                    },
                                                    ".MuiInputAdornment-root": {
                                                      "margin-right": "-2px",
                                                    },
                                                    svg: {
                                                      "margin-left": "0px",
                                                    },
                                                  }}
                                                  value={
                                                    valorInputColuna[coluna.key]?.dataInclusao
                                                      ? dayjs(
                                                          valorInputColuna[coluna.key]
                                                            ?.dataInclusao,
                                                        )
                                                      : null
                                                  }
                                                  onChange={(e) =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColuna(e, coluna.key)
                                                  }
                                                  onAccept={(e) =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColuna(e, coluna.key)
                                                  }
                                                />
                                              ) : coluna.campoDeDataInicial ? (
                                                comHora === true ? (
                                                  <>
                                                    <CampoDeData
                                                      mostrarHora={comHora}
                                                      sx={{
                                                        ".MuiInputBase-input": {
                                                          height: "auto",
                                                        },
                                                        ".MuiInputAdornment-root": {
                                                          "margin-right": "-12px",
                                                        },
                                                        svg: {
                                                          "margin-left": "0px",
                                                        },
                                                      }}
                                                      value={
                                                        valorInputColuna[coluna.key]?.dataInicial
                                                          ? dayjs(
                                                              valorInputColuna[coluna.key]
                                                                ?.dataInicial,
                                                            )
                                                          : null
                                                      }
                                                      onChange={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                      onAccept={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                      error={false}
                                                    />
                                                  </>
                                                ) : (
                                                  <>
                                                    <CampoDeDataSemHora
                                                      sx={{
                                                        ".MuiInputBase-input": {
                                                          height: "auto",
                                                        },
                                                        ".MuiInputAdornment-root": {
                                                          "margin-right": "-12px",
                                                        },
                                                        svg: {
                                                          "margin-left": "0px",
                                                        },
                                                      }}
                                                      value={
                                                        valorInputColuna[coluna.key]?.dataInicial
                                                          ? dayjs(
                                                              valorInputColuna[coluna.key]
                                                                ?.dataInicial,
                                                            )
                                                          : null
                                                      }
                                                      onChange={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                      onAccept={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                    />
                                                  </>
                                                )
                                              ) : coluna.campoDeDataFinal ? (
                                                comHora === true ? (
                                                  <>
                                                    <CampoDeData
                                                      mostrarHora={comHora}
                                                      sx={{
                                                        ".MuiInputBase-input": {
                                                          height: "auto",
                                                        },
                                                        ".MuiInputAdornment-root": {
                                                          "margin-right": "-12px",
                                                        },
                                                        svg: {
                                                          "margin-left": "0px",
                                                        },
                                                      }}
                                                      value={
                                                        valorInputColuna[coluna.key]?.dataFinal
                                                          ? dayjs(
                                                              valorInputColuna[coluna.key]
                                                                ?.dataFinal,
                                                            )
                                                          : null
                                                      }
                                                      onChange={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                      onAccept={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                      error={false}
                                                    />
                                                  </>
                                                ) : (
                                                  <>
                                                    <CampoDeDataSemHora
                                                      sx={{
                                                        ".MuiInputBase-input": {
                                                          height: "auto",
                                                        },
                                                        ".MuiInputAdornment-root": {
                                                          "margin-right": "-12px",
                                                        },
                                                        svg: {
                                                          "margin-left": "0px",
                                                        },
                                                      }}
                                                      value={
                                                        valorInputColuna[coluna.key]?.dataFinal
                                                          ? dayjs(
                                                              valorInputColuna[coluna.key]
                                                                ?.dataFinal,
                                                            )
                                                          : null
                                                      }
                                                      onChange={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                      onAccept={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(e, coluna.key)
                                                      }
                                                    />
                                                  </>
                                                )
                                              ) : coluna.checkbox ? (
                                                <S.CustomizacaoCheckbox>
                                                  {coluna.checkboxTitulo}
                                                  <CheckBox
                                                    tamanho={"MD"}
                                                    valor={
                                                      !coluna.checkboxValorInverso
                                                        ? valorInputColuna[coluna.key]
                                                          ? false
                                                          : true
                                                        : valorInputColuna[coluna.key]
                                                          ? true
                                                          : false
                                                    }
                                                    onClick={(e) => {
                                                      coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(
                                                          valorInputColuna[coluna.key]
                                                            ? false
                                                            : true,
                                                          coluna.key,
                                                        );
                                                    }}
                                                  />
                                                </S.CustomizacaoCheckbox>
                                              ) : (
                                                <CampoDeTexto
                                                  sx={{
                                                    "& .MuiInputBase-root": {
                                                      padding: "0 5px",
                                                      margin: 0,
                                                    },
                                                    ".MuiSelect-select": {
                                                      marginLeft: "5px",
                                                    },
                                                    ".MuiInputBase-input": {
                                                      padding: "17px",
                                                      fontSize: "15px",
                                                    },
                                                  }}
                                                  fundoTransparente
                                                  placeholder={coluna.placeholder || "Digite aqui"}
                                                  maxlength={coluna.maxlength}
                                                  value={
                                                    coluna.key == "cnpj" ||
                                                    coluna.key == "inscricaoFederal"
                                                      ? formatarInscricaoFederal(
                                                          valorInputColuna?.[coluna.key],
                                                        )
                                                      : valorInputColuna?.[coluna.key]
                                                  }
                                                  onChange={(e) =>
                                                    coluna.key !== undefined &&
                                                    pegarValorFiltroColuna(
                                                      coluna.key == "cnpj" ||
                                                        coluna.key == "inscricaoFederal"
                                                        ? formatarInscricaoFederal(e.target.value)
                                                        : e.target.value,
                                                      coluna.key,
                                                    )
                                                  }
                                                />
                                              )}
                                            </>
                                          )}
                                      </S.CampoFiltroColuna>
                                    </S.ContainerColunaComFiltro>
                                  </HeaderCell>
                                ) : (coluna.ordenacao === false || variante?.ordenacao === false) &&
                                  !recebimentoMercadoria ? (
                                  <HeaderCell
                                    key={coluna.titulo + index}
                                    resize={coluna.resize}
                                    style={
                                      headerSecundario
                                        ? estiloAgrupado(indexOriginal)
                                        : {
                                            background: porModulo && indexOriginal > 4 && "#1B7DB5",
                                            color:
                                              porModulo &&
                                              indexOriginal > 4 &&
                                              tema.cores.neutral100,
                                          }
                                    }
                                  >
                                    {porModulo ? (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          justifyContent: "space-between",
                                          height: "100%",
                                          minHeight: "64px",
                                        }}
                                      >
                                        {indexOriginal === 0 ? (
                                          <div style={{ marginBottom: 8 }}>
                                            <S.TextoHeaderTabela>Dia</S.TextoHeaderTabela>
                                          </div>
                                        ) : indexOriginal === 5 ? (
                                          <div style={{ marginBottom: 8 }}>
                                            <S.TextoHeaderTabela
                                              style={{
                                                color: tema.cores.neutral100,
                                              }}
                                            >
                                              {textoPorModulo}
                                            </S.TextoHeaderTabela>
                                          </div>
                                        ) : indexOriginal > 0 && indexOriginal < 5 ? (
                                          <div style={{ marginBottom: 40 }} />
                                        ) : indexOriginal > 5 ? (
                                          <div style={{ marginBottom: 40 }} />
                                        ) : null}
                                        <div
                                          style={{
                                            marginBottom: 0,
                                            marginTop: "auto",
                                          }}
                                        >
                                          {statusPesquisaColuna &&
                                          coluna.titulo &&
                                          String(coluna?.titulo)?.trim() ? (
                                            <S.ContainerColunaComFiltro>
                                              <S.TituloColuna
                                                style={{
                                                  color:
                                                    indexOriginal > 4
                                                      ? tema.cores.neutral100
                                                      : "inherit",
                                                }}
                                              >
                                                {coluna.key !== "checkbox" &&
                                                  String(coluna?.titulo)?.trim() &&
                                                  (indexOriginal <= 4
                                                    ? coluna.titulo
                                                        .toLowerCase()
                                                        .split(" ")
                                                        .map(
                                                          (word) =>
                                                            word.charAt(0).toUpperCase() +
                                                            word.slice(1),
                                                        )
                                                        .join(" ")
                                                    : coluna.titulo)}
                                              </S.TituloColuna>
                                              <S.CampoFiltroColuna>
                                                {(variante === "completa" ||
                                                  variante?.filtroColuna) &&
                                                  statusPesquisaColuna &&
                                                  !coluna.diaDaSemana &&
                                                  coluna?.campoDeTexto !== false && (
                                                    <CampoDeTexto
                                                      sx={{
                                                        "& .MuiInputBase-root": {
                                                          padding: "0 5px",
                                                          margin: 0,
                                                        },
                                                        ".MuiSelect-select": {
                                                          marginLeft: "5px",
                                                        },
                                                        ".MuiInputBase-input": {
                                                          padding: "17px",
                                                          fontSize: "15px",
                                                        },
                                                      }}
                                                      fundoTransparente
                                                      placeholder="Digite aqui"
                                                      value={
                                                        valorInputColuna?.[coluna.key] ??
                                                        textosInputs?.[coluna.key]
                                                      }
                                                      key={index}
                                                      onChange={(e) =>
                                                        coluna.key !== undefined &&
                                                        pegarValorFiltroColuna(
                                                          e.target.value,
                                                          coluna.key,
                                                        )
                                                      }
                                                    />
                                                  )}
                                              </S.CampoFiltroColuna>
                                            </S.ContainerColunaComFiltro>
                                          ) : (
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "flex-end",
                                                justifyContent: "flex-start",
                                                color:
                                                  indexOriginal > 4
                                                    ? tema.cores.neutral100
                                                    : "inherit",
                                                fontSize: "14px",
                                                fontWeight: 500,
                                              }}
                                            >
                                              {coluna.titulo &&
                                                coluna.key !== "checkbox" &&
                                                String(coluna?.titulo)?.trim() &&
                                                (indexOriginal <= 4
                                                  ? coluna.titulo
                                                      .toLowerCase()
                                                      .split(" ")
                                                      .map(
                                                        (word) =>
                                                          word.charAt(0).toUpperCase() +
                                                          word.slice(1),
                                                      )
                                                      .join(" ")
                                                  : coluna.titulo)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        {statusPesquisaColuna &&
                                        coluna.titulo &&
                                        String(coluna?.titulo)?.trim() ? (
                                          <S.ContainerColunaComFiltro>
                                            <S.TituloColuna>
                                              {coluna.key !== "checkbox" &&
                                                String(coluna?.titulo)?.trim() &&
                                                coluna.titulo}
                                            </S.TituloColuna>
                                            <S.CampoFiltroColuna>
                                              {(variante === "completa" ||
                                                variante?.filtroColuna) &&
                                                statusPesquisaColuna &&
                                                !coluna.diaDaSemana &&
                                                coluna?.campoDeTexto !== false && (
                                                  <CampoDeTexto
                                                    sx={{
                                                      "& .MuiInputBase-root": {
                                                        padding: "0 5px",
                                                        margin: 0,
                                                      },
                                                      ".MuiSelect-select": {
                                                        marginLeft: "5px",
                                                      },
                                                      ".MuiInputBase-input": {
                                                        padding: "17px",
                                                        fontSize: "15px",
                                                      },
                                                    }}
                                                    fundoTransparente
                                                    placeholder="Digite aqui"
                                                    value={
                                                      valorInputColuna?.[coluna.key] ??
                                                      textosInputs?.[coluna.key]
                                                    }
                                                    key={index}
                                                    onChange={(e) =>
                                                      coluna.key !== undefined &&
                                                      pegarValorFiltroColuna(
                                                        e.target.value,
                                                        coluna.key,
                                                      )
                                                    }
                                                  />
                                                )}
                                            </S.CampoFiltroColuna>
                                          </S.ContainerColunaComFiltro>
                                        ) : (
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "flex-start",
                                              height: "100%",
                                            }}
                                          >
                                            {coluna.titulo &&
                                              coluna.key !== "checkbox" &&
                                              String(coluna?.titulo)?.trim() &&
                                              coluna.titulo}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </HeaderCell>
                                ) : recebimentoMercadoria && coluna.ordenacao === false ? (
                                  <HeaderCell
                                    key={coluna.titulo + index}
                                    sortKey={coluna.sortKey || coluna.key}
                                    resize={coluna.resize}
                                    style={estiloAgrupado(indexOriginal)}
                                  >
                                    {statusPesquisaColuna && variante === "completa" ? (
                                      <S.ContainerColunaComFiltro>
                                        <S.TituloColuna $semQuebra={coluna.semQuebra}>
                                          {coluna.titulo}
                                        </S.TituloColuna>
                                        <S.CampoFiltroColuna>
                                          <CampoDeTexto
                                            sx={{
                                              "& .MuiInputBase-root": {
                                                padding: "0 5px",
                                                margin: 0,
                                              },
                                              ".MuiSelect-select": {
                                                marginLeft: "5px",
                                              },
                                              ".MuiInputBase-input": {
                                                padding: "17px",
                                                fontSize: "15px",
                                              },
                                            }}
                                            fundoTransparente
                                            onChange={(e) =>
                                              pegarValorFiltroColuna(e.target.value, coluna.key)
                                            }
                                          />
                                        </S.CampoFiltroColuna>
                                      </S.ContainerColunaComFiltro>
                                    ) : (
                                      coluna.titulo
                                    )}
                                  </HeaderCell>
                                ) : (
                                  <HeaderCellSort
                                    key={coluna.titulo + index}
                                    sortKey={coluna.sortKey || coluna.key}
                                    resize={coluna.resize}
                                    style={estiloAgrupado(indexOriginal)}
                                  >
                                    {statusPesquisaColuna && variante === "completa" ? (
                                      <S.ContainerColunaComFiltro>
                                      <S.TituloColuna $semQuebra={coluna.semQuebra}>
                                        {coluna.titulo}
                                      </S.TituloColuna>
                                        <S.CampoFiltroColuna>
                                          <CampoDeTexto
                                            sx={{
                                              "& .MuiInputBase-root": {
                                                padding: "0 5px",
                                                margin: 0,
                                              },
                                              ".MuiSelect-select": {
                                                marginLeft: "5px",
                                              },
                                              ".MuiInputBase-input": {
                                                padding: "17px",
                                                fontSize: "15px",
                                              },
                                            }}
                                            fundoTransparente
                                            onChange={(e) =>
                                              pegarValorFiltroColuna(e.target.value, coluna.key)
                                            }
                                          />
                                        </S.CampoFiltroColuna>
                                      </S.ContainerColunaComFiltro>
                                    ) : (
                                      coluna.titulo
                                    )}
                                  </HeaderCellSort>
                                )}
                              </Fragment>
                            );
                          })}
                    </HeaderRow>
                  </Header>

                  <Body>
                    {tableList.length === 0 && manterEstruturaTabelaSemResultados && (
                      <Row item={{ id: "tabela-sem-resultados" }}>
                        <Cell
                          style={{
                            gridColumn: "1 / -1",
                            padding: 0,
                          }}
                        >
                          <S.TextoFiltroSemResultado style={{ margin: 0, width: "100%" }}>
                            {textoNenhumResultado}
                          </S.TextoFiltroSemResultado>
                        </Cell>
                      </Row>
                    )}
                    {tableList.map((item, index) => (
                      <Fragment key={item.id ? item.id : index}>
                        <S.StyledRow
                          item={item}
                          grifarLinhaInventario={!!item.grifarLinhaInventario}
                          onClick={() => {
                            (item?.itensTabelaSecundaria !== null ||
                              item?.itensTabelaSecundariaCards !== null) &&
                              handleExpand(item);

                            variante?.treeTabela
                              ? dadosTabela.eventoCliqueLinha &&
                                dadosTabela.eventoCliqueLinha(linhaSelecionada)
                              : dadosTabela.eventoCliqueLinha &&
                                dadosTabela.eventoCliqueLinha(dadosTabela.nodes[index].parametro);

                            if (!variante?.desabilitarSelecaoLinha) {
                              if (linhaSelecionada?.id !== item.id) {
                                linhaSelecionada = item;
                              } else {
                                linhaSelecionada = null;
                              }
                            }
                          }}

                          style={{
                            cursor: dadosTabela?.navegacaoLinha
                              ? "pointer"
                              : (dadosTabela?.colunasTabelaSecundaria?.length ||
                                    dadosTabela.eventoCliqueLinha) &&
                                  (variante.accordionTabela ||
                                    variante.accordionCards ||
                                    dadosTabela.eventoCliqueLinha) &&
                                  (item.itensTabelaSecundaria || dadosTabela.eventoCliqueLinha)
                                ? "pointer"
                                : "",
                            backgroundColor:
                              (variante?.hoverLinhas &&
                                !variante?.desabilitarSelecaoLinha &&
                                linhaSelecionada?.id === item.id) ||
                              (variante?.treeTabela && linhaSelecionada?.id === item.id) ||
                              (variante?.treeTabela && linhaValorTela?.id === item.id)
                                ? tema.cores.hoverTabela
                                : null,
                          }}
                        >
                          {dadosTabela &&
                            dadosTabela.colunas
                              .map((celula, indexOriginal) => ({
                                celula,
                                indexOriginal,
                              }))
                              .filter(({ celula }) => !colunasEscondidas.includes(celula.titulo))
                              .map(({ celula, indexOriginal }, index) => {
                                return celula.titulo === "Checkbox" || celula.key === "checkbox" ? (
                                  <Cell key={celula.titulo + index + "key"}>
                                    <CheckBox
                                      tamanho={"MD"}
                                      valor={select.state.ids.includes(item.id)}
                                      onClick={(e) => {
                                        select.fns.onToggleById(item.id);
                                        e.stopPropagation();
                                      }}
                                    />
                                  </Cell>
                                ) : (variante === "accordionTabela" || variante?.treeTabela) &&
                                  indexOriginal === 0 ? (
                                  <CellTree
                                    key={celula.titulo + index + "key"}
                                    item={item}
                                    style={estiloCelulaAgrupada(indexOriginal)}
                                    onDoubleClick={(e) => {
                                      if (dadosTabela?.navegacaoLinha) {
                                        if (celula.desativarDuploClique) {
                                          e.stopPropagation();
                                        } else {
                                          dadosTabela.navegacaoLinha(item, index);
                                        }
                                      }
                                    }}
                                  >
                                    {item[celula.key] || "-"}
                                  </CellTree>
                                ) : variante === "accordionTabela" ? (
                                  <Cell
                                    key={celula.titulo + index + "key"}
                                    style={estiloCelulaAgrupada(indexOriginal)}
                                    onDoubleClick={(e) => {
                                      if (dadosTabela?.navegacaoLinha) {
                                        if (celula.desativarDuploClique) {
                                          e.stopPropagation();
                                        } else {
                                          dadosTabela.navegacaoLinha(item, index);
                                        }
                                      }
                                    }}
                                  >
                                    {item[celula.key] || "-"}
                                  </Cell>
                                ) : celula.titulo === "" && !variante.tabelaSecundariaSobDemanda ? (
                                  <Cell
                                    key={celula.titulo + index + "key"}
                                    style={estiloCelulaAgrupada(indexOriginal)}
                                    onDoubleClick={(e) => {
                                      if (dadosTabela?.navegacaoLinha) {
                                        if (celula.desativarDuploClique) {
                                          e.stopPropagation();
                                        } else {
                                          dadosTabela.navegacaoLinha(item, index);
                                        }
                                      }
                                    }}
                                  >
                                    {carregandoTabela ? (
                                      <Skeleton
                                        sx={{
                                          height: "18.67px",
                                          transform: "none",
                                        }}
                                        animation="wave"
                                      />
                                    ) : (
                                      <>
                                        {(dadosTabela?.itensTabelaSecundaria?.length ||
                                          dadosTabela?.itensTabelaSecundariaCards?.length) &&
                                          (variante.accordionTabela || variante.accordionCards) &&
                                          ids.includes(item.id)}
                                        {((dadosTabela?.colunasTabelaSecundaria?.length ||
                                          dadosTabela?.colunasTabelaSecundariaCards?.length) &&
                                          (variante.accordionTabela || variante.accordionCards) &&
                                          ids.includes(item.id) &&
                                          (item.itensTabelaSecundaria ||
                                            item.itensTabelaSecundariaCards)) ||
                                        (variante?.accordionComponenteCustomizado &&
                                          ids.includes(item.id)) ? (
                                          <S.StyledMinus weight="bold" />
                                        ) : item.itensTabelaSecundaria ||
                                          item.itensTabelaSecundariaCards ||
                                          (variante?.accordionComponenteCustomizado &&
                                            !ids.includes(item.id)) ? (
                                          <S.StyledPlus weight="bold" />
                                        ) : (
                                          ""
                                        )}
                                      </>
                                    )}
                                  </Cell>
                                ) : celula.titulo === "" && variante.tabelaSecundariaSobDemanda ? (
                                  <Cell
                                    key={celula.titulo + index + "key"}
                                    style={estiloCelulaAgrupada(indexOriginal)}
                                    onDoubleClick={(e) => {
                                      if (dadosTabela?.navegacaoLinha) {
                                        if (celula.desativarDuploClique) {
                                          e.stopPropagation();
                                        } else {
                                          dadosTabela.navegacaoLinha(item, index);
                                        }
                                      }
                                    }}
                                  >
                                    {carregandoTabela ? (
                                      <Skeleton
                                        sx={{
                                          height: "18.67px",
                                          transform: "none",
                                        }}
                                        animation="wave"
                                      />
                                    ) : (
                                      <>
                                        {ids.includes(item.id) ? (
                                          <S.StyledMinus weight="bold" />
                                        ) : (
                                          <S.StyledPlus weight="bold" />
                                        )}
                                      </>
                                    )}
                                  </Cell>
                                ) : (
                                  <Fragment key={celula.titulo + index + "key"}>
                                    <Cell
                                      style={estiloCelulaAgrupada(indexOriginal)}
                                      onDoubleClick={(e) => {
                                        if (dadosTabela?.navegacaoLinha) {
                                          if (celula.desativarDuploClique) {
                                            e.stopPropagation();
                                          } else {
                                            dadosTabela.navegacaoLinha(item, index);
                                          }
                                        }
                                      }}
                                    >
                                      {carregandoTabela ? (
                                        <Skeleton
                                          sx={{
                                            height: "18.67px",
                                            transform: "none",
                                          }}
                                          animation="wave"
                                        />
                                      ) : (
                                        item[celula.key] || "-"
                                      )}
                                    </Cell>
                                  </Fragment>
                                );
                              })}
                        </S.StyledRow>
                        {ids.includes(item.id) &&
                          variante?.accordionTabela &&
                          variante.tabelaSecundariaSobDemanda && (
                            <div
                              style={{
                                display: "flex",
                                gridColumn: "1 / -1",
                              }}
                            >
                              <S.ContainerTabelaSecundaria style={{ flex: "1" }}>
                                <S.ContainerTextoTabelaSecundaria>
                                  {dadosTabela.componenteHeaderTabelaSecundaria &&
                                    dadosTabela.componenteHeaderTabelaSecundaria(item, dadosTabela)}
                                  {variante?.textoTabelaSecundaria}
                                </S.ContainerTextoTabelaSecundaria>
                                <TabelaSimples
                                  dados={dadosTabela}
                                  item={dadosTabela.tabelaSecundariaSobDemanda[item.id]}
                                />
                                {dadosTabela.componenteFooterTabelaSecundaria &&
                                  dadosTabela.componenteFooterTabelaSecundaria(item, dadosTabela)}
                              </S.ContainerTabelaSecundaria>
                            </div>
                          )}
                        {ids.includes(item.id) &&
                          variante?.accordionTabela &&
                          (item?.itensTabelaSecundaria?.length > 0 ||
                            variante?.accordionComponenteCustomizado) &&
                          !variante.tabelaSecundariaSobDemanda && (
                            <div
                              style={{
                                display: "flex",
                                gridColumn: "1 / -1",
                              }}
                            >
                              {variante?.accordionComponenteCustomizado ? (
                                item.accordionComponenteCustomizado
                              ) : (
                                <S.ContainerTabelaSecundaria style={{ flex: "1" }}>
                                  <S.ContainerTextoTabelaSecundaria>
                                    {variante?.textoTabelaSecundaria}
                                  </S.ContainerTextoTabelaSecundaria>
                                  <TabelaSimples dados={dadosTabela} item={item} />
                                </S.ContainerTabelaSecundaria>
                              )}
                            </div>
                          )}
                        {ids.includes(item.id) &&
                          variante?.accordionCards &&
                          item?.itensTabelaSecundariaCards?.length > 0 && (
                            <S.AccordionCard style={{ display: "flex", gridColumn: "1 / -1" }}>
                              <div style={{ flex: "1" }}>
                                <h1>Detalhe por tabela de preço</h1>
                                <S.ContainerCards>
                                  {item?.itensTabelaSecundariaCards?.map((content) => {
                                    return (
                                      <S.CardTabelaPreco>
                                        {dadosTabela?.colunasTabelaSecundariaCards.map((coluna) => {
                                          return (
                                            <div>
                                              {coluna.key === "precoVenda" ? (
                                                <p>{formatarReal(content[coluna.key])}</p>
                                              ) : (
                                                <p>{content[coluna.key]}</p>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </S.CardTabelaPreco>
                                    );
                                  })}
                                </S.ContainerCards>
                              </div>
                            </S.AccordionCard>
                          )}
                      </Fragment>
                    ))}
                  </Body>

                  <Footer>
                    <FooterRow>
                      {dadosTabela.dadosFooter &&
                        dadosTabela.dadosFooter.map((item, index) => {
                          return (
                            <FooterCell
                              key={item + index + "key"}
                              style={estiloCelulaAgrupada(index)}
                            >
                              {carregandoTabela ? (
                                <Skeleton
                                  sx={{
                                    height: "18.67px",
                                    transform: "none",
                                  }}
                                  animation="wave"
                                />
                              ) : (
                                item
                              )}
                            </FooterCell>
                          );
                        })}
                      {manterEstruturaTabelaSemResultados && !dadosTabela.dadosFooter?.length && (
                        <FooterCell
                          aria-hidden="true"
                          style={{
                            gridColumn: "1 / -1",
                            height: 1,
                            minHeight: 1,
                            padding: 0,
                            border: 0,
                            overflow: "hidden",
                          }}
                        />
                      )}
                    </FooterRow>
                  </Footer>
                </>
              )}
            </Table>
          )}
        </S.ContainerTabela>

        {!variante?.loadingBodyTabela &&
          !dadosMokados &&
          (possuiPaginas || manterEstruturaTabelaSemResultados) && (
            <S.ContainerPaginacao>
              {largura <= 768 || (
                <S.ContainerPaginaAtualTextfield>
                  {dadosTabela.totalElementos >= 0 && (
                    <>
                      <span>{t("pagination.itemsPerPage")} </span>

                      <S.StyledCampoDeSelecao
                        campoVazio={false}
                        disabled={itemUnico}
                        valor={quantidadeItens}
                        items={quantidadeItensMostrarTabela}
                        style={{
                          width: 97,
                          margin: 0,
                          fontSize: 15,
                          textAlign: "center",
                          height: "33px",
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            padding: "0 5px",
                            margin: 0,
                            marginTop: "20px",
                            height: "33px",
                          },
                          ".MuiInputBase-input": {
                            fontSize: "15px",
                          },
                          "& .MuiFormControl-root": {
                            height: "33px",
                          },
                          "& .MuiSelect-select": {
                            paddingBottom: "10px",
                          },
                          "& .css-17idfle-MuiSelect-select-MuiInputBase-input-MuiInput-input:focus":
                            {
                              backgroundColor: "transparent",
                            },
                        }}
                        onChange={(e) => trocarQuantidadeItensTabela(e.target.value)}
                      />

                      {typeof dadosTabela.totalElementos === "number" &&
                        (manterEstruturaTabelaSemResultados ||
                          Number(dadosTabela.totalElementos) >= Number(quantidadeItens)) && (
                          <span
                            style={{
                              marginLeft: "16px",
                              fontSize: "16px",
                              color: tema.adaptativo("neutral10", "neutral100"),
                            }}
                          >
                            {dadosTabela.totalElementos}{" "}
                            {dadosTabela.totalElementos !== 1
                              ? t("pagination.records")
                              : t("pagination.record")}
                          </span>
                        )}
                    </>
                  )}
                </S.ContainerPaginaAtualTextfield>
              )}

              <S.ContainerPaginas>
                <S.IconeBotaoPage
                  type="button"
                  disabled={!possuiPaginas || pagination.state.page === 0}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <S.IconsFirstAndLastPage
                    className="material-symbols-rounded"
                    onClick={() => {
                      if (possuiPaginas && pagination.state.page !== 0) {
                        mudarPaginaTabela(0);
                      }
                    }}
                  >
                    first_page
                  </S.IconsFirstAndLastPage>
                </S.IconeBotaoPage>
                <S.IconeBotaoPage
                  type="button"
                  disabled={!possuiPaginas || pagination.state.page === 0}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <FontAwesomeIcon
                    icon={faAngleLeft}
                    color={tema.cores.actionActive}
                    style={{ width: 24, height: 24 }}
                    onClick={() => {
                      if (possuiPaginas && pagination.state.page >= 1) {
                        mudarPaginaTabela(dadosTabela.pagina - 1);
                      }
                    }}
                  />
                </S.IconeBotaoPage>
                {possuiPaginas &&
                  gerarBotoes(
                    dadosTabela.pagina,
                    dadosTabela.paginas,
                    dadosTabela.maxBotoes,
                    dadosTabela.mudarPagina,
                  )}
                <S.IconeBotaoPage
                  type="button"
                  disabled={!possuiPaginas || pagination.state.page === dadosTabela.paginas - 1}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <FontAwesomeIcon
                    icon={faAngleRight}
                    color={tema.cores.actionActive}
                    style={{ width: 24, height: 24 }}
                    onClick={() => {
                      if (possuiPaginas && dadosTabela.paginas - 1 > pagination.state.page) {
                        mudarPaginaTabela(dadosTabela.pagina + 1);
                      }
                    }}
                  />
                </S.IconeBotaoPage>
                <S.IconeBotaoPage
                  type="button"
                  disabled={!possuiPaginas || pagination.state.page === dadosTabela.paginas - 1}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <S.IconsFirstAndLastPage
                    className="material-symbols-rounded"
                    onClick={() => {
                      if (possuiPaginas && pagination.state.page !== dadosTabela.paginas - 1) {
                        mudarPaginaTabela(dadosTabela.paginas - 1);
                      }
                    }}
                  >
                    last_page
                  </S.IconsFirstAndLastPage>
                </S.IconeBotaoPage>
              </S.ContainerPaginas>
            </S.ContainerPaginacao>
          )}
      </S.ContainerTabelaWrapper>

      {!variante?.loadingBodyTabela &&
        !dadosTabela.nodes?.length &&
        !manterEstruturaTabelaSemResultados && (
          <S.TextoFiltroSemResultado>{textoNenhumResultado}</S.TextoFiltroSemResultado>
        )}
    </S.StyledContainerGlobal>
  );
}

export function TabelaLib(props) {
  const buscaInicialExecutadaRef = useRef(false);
  const { dadosAuth } = useAuth();
  const idEmpresa = dadosAuth?.empresaDTO?.id;

  useEffect(() => {
    const valoresColunaInicial =
      props.variante?.funcaoBuscaInicial ||
      props.buscarDadosIniciais ||
      props.dadosTabela?.valoresColuna;

    const tabelaCarregada = props.dadosTabela?.colunas && Array.isArray(props.dadosTabela?.nodes);

    if (buscaInicialExecutadaRef.current || tabelaCarregada || !valoresColunaInicial) {
      return;
    }

    buscaInicialExecutadaRef.current = true;

    const filtroGeralSalvo = carregarStorageTabela(
      gerarChaveStorageTabela("tabela_filtro_geral", props.identificadorTabela, idEmpresa),
    );
    const filtroColunasSalvo = carregarStorageTabela(
      gerarChaveStorageTabela("tabela_filtro_colunas", props.identificadorTabela, idEmpresa),
    );

    const valorFiltroGeral = filtroGeralSalvo?.valorFiltroGeral || "";
    const filtrosColunas = filtroColunasSalvo?.filtrosColunas || {};

    valoresColunaInicial({
      data: filtrosColunas?.data || null,
      diaDaSemana: null,
      ordenacao: {},
      conteudoBody: {
        palavraChave: valorFiltroGeral,
        palavraChaveEstado: valorFiltroGeral,
        colunasValores: filtrosColunas,
        valoresSelect: filtrosColunas,
      },
      valoresInput: [],
      linhasPorPagina: props.variante?.tamanhoPaginaInicial
        ? String(props.variante.tamanhoPaginaInicial)
        : "10",
      colunasVisiveisKey: [],
    });
  }, [
    props.valoresColuna,
    props.variante?.funcaoBuscaInicial,
    props.buscarDadosIniciais,
    props.dadosTabela?.valoresColuna,
    props.dadosTabela?.colunas,
    props.dadosTabela?.nodes,
    props.identificadorTabela,
    props.variante?.tamanhoPaginaInicial,
  ]);

  if (!props.dadosTabela || !props.dadosTabela.colunas || !props.dadosTabela.nodes) {
    return <Carregando />;
  }

  return <TabelaLibComponent {...props} />;
}
